import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobsService, sitesService, auditorsService, servicesService, clientsService, offlineService, notificationsService, sitePricingService, settingsService } from '../services';
import { LockBadge, LockToggleButton, LockLegend, showLockedToast, isLockedError } from '../components/JobLock';
import { useAuth } from '../contexts/AuthContext';
import RegionFilter from '../components/RegionFilter';
import { useOffline } from '../contexts/OfflineContext';
import { useSettings } from '../contexts/SettingsContext';
import SiteCustomerCombobox from '../components/SiteCustomerCombobox';
import DuplicateJobConfirmDialog from '../components/DuplicateJobConfirmDialog';
import UnpublishJobsDialog from '../components/UnpublishJobsDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, Calendar, Loader2, CheckCircle, XCircle, Pencil, Trash2, Users, User, Filter, CalendarRange, Copy, CopyPlus, StickyNote, CloudOff, AlertTriangle, Send, Bell, Clock, Mail, AlertCircle, Grid3X3, Banknote, RefreshCw, ExternalLink, ChevronDown, ChevronRight, Check, FileEdit, ClipboardList, X, Eye, EyeOff, ClipboardCheck } from 'lucide-react';
import { format, addDays, addMonths, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, parseISO, subMonths } from 'date-fns';
import { getApiError, cn } from '@/lib/utils';
import api from '../services/api';
import ApprovePublishDialog from '../components/BatchPublishReviewDialog';

const statusColors = {
  scheduled: 'default',
  completed: 'secondary',
  cancelled: 'destructive'
};

const statusLabels = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const initialFormData = {
  site_id: '',
  service_id: '',
  auditor_ids: [],
  client_reference: '',
  notes: '',
  scheduled_date: '',  // Start date
  end_date: '',  // End date (optional, for multi-day jobs)
  forecast_override: '',  // Override customer-level forecast for this job
  status: 'scheduled',  // Status (only for edit mode)
  completed_on: '',  // Completion date (required when status = completed)
  delay_justified: false,  // Delay justified flag
  delay_justification_note: '',  // Justification note (required when delay_justified = true)
  event_type: 'CLIENT_JOB'  // Type of entry: CLIENT_JOB, LEAVE_HOLIDAY, LEAVE_SICK, LEAVE_COMPASSIONATE
};

// A job only has genuine "Per-Scope Completion" when it covers 2+ scopes
// (Food + Beverage). A single leftover completed `scope_completions` row
// — e.g. a job that was once per-scope but later reduced to one line
// item — must NOT still be treated as per-scope; completed rows are
// intentionally never deleted (audit history is immutable), so a plain
// `.length >= 1` check misclassifies these. See job investigation:
// Oxfordshire Golf Hotel & Spa, 2 Sep 2026, job 1746 (Food-only line,
// one leftover completed FOOD scope row).
const isPerScopeJob = (job) => Array.isArray(job?.scope_completions) && job.scope_completions.length >= 2;

// ─── Block Booking pure helpers ─────────────────────────────────────────────
// Exported for direct testing; both helpers are used by SchedulePage internally.
// Pure functions — no React state, no side effects, no network calls.

/**
 * Assigns a fresh UUID requestIdempotencyKey to each NEW pair.
 * Pairs already present in existingPairs keep their original key unchanged.
 * This is the canonical key-assignment logic — do not inline elsewhere.
 *
 * @param {Array} pairs        - pairs returned by BlockBookingDialog.handleSave
 * @param {Array} existingPairs - current blockPairs state in parent
 * @returns {Array} new pair array with keys assigned / preserved
 */
export function applyBlockPairKeys(pairs, existingPairs) {
  const existingKeys = Object.fromEntries(existingPairs.map(p => [p.id, p.requestIdempotencyKey]));
  return pairs.map(p => ({
    ...p,
    requestIdempotencyKey: existingKeys[p.id] || crypto.randomUUID(),
  }));
}

/**
 * Builds the per-pair POST payload for /batch-schedule/job.
 * batchBase carries the shared booking fields (site_id, service_id,
 * auditor_ids, event_type, notes, client_reference).
 * client_reference is a user-entered business reference — preserved
 * independently of request_idempotency_key on every job in the block.
 *
 * @param {object} pair      - a single blockPairs entry
 * @param {object} batchBase - shared fields from SchedulePage form state
 * @returns {object} the POST payload for this pair
 */
export function buildBlockBookingPayload(pair, batchBase, bbBatchId, blockGroupKey) {
  return {
    ...batchBase,
    scheduled_date:          pair.start,
    end_date:                pair.end || pair.start,
    request_idempotency_key: pair.requestIdempotencyKey || null,
    // BB coherent batch — pass from second pair onward so all pairs join
    // the same DRAFT batch created by the first pair.  Null for pair 1.
    schedule_batch_id:       bbBatchId || null,
    // BB coherent identity — the SAME client-generated UUID on every pair
    // of a single Block Booking (even pair 1).  Lets the backend validate
    // fail-closed and reuse the first-pair batch on retry.  Null for
    // ordinary (non-BB) job posts.
    block_group_key:         blockGroupKey || null,
  };
}

// ─── BlockBookingDialog ──────────────────────────────────────────────────────
// Separate child dialog for editing date pairs without creating any jobs.
// Save returns the validated pair array to the parent; Cancel discards edits.
// Keeping this outside SchedulePage avoids unnecessary re-renders and keeps
// the pair editing state self-contained.
export function BlockBookingDialog({ open, onOpenChange, initialPairs, onSave }) {
  const [rows, setRows]           = React.useState([]);
  const [rowErrors, setRowErrors] = React.useState({});

  // Seed rows every time the dialog opens; also clear stale errors
  React.useEffect(() => {
    if (!open) return;
    setRowErrors({});
    setRows(
      initialPairs.length > 0
        ? initialPairs.map(p => ({ ...p }))
        : [{ id: crypto.randomUUID(), start: '', end: '' }]
    );
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const addRow    = () => setRows(prev => [...prev, { id: crypto.randomUUID(), start: '', end: '' }]);
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    // Clear per-row error whenever a date field is edited
    setRowErrors(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSave = () => {
    // Validate: end must not precede start when both are provided
    const reversed = rows.filter(r => r.start && r.end && r.end < r.start);
    if (reversed.length > 0) {
      setRowErrors(
        Object.fromEntries(reversed.map(r => [r.id, 'End date must be on or after start date']))
      );
      return;
    }
    const valid = rows.filter(r => r.start);
    if (!valid.length) return;
    setRowErrors({});
    onSave(valid.map(r => ({ ...r, end: r.end || r.start })));
    onOpenChange(false);
  };

  const canSave = rows.some(r => r.start);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="block-booking-dialog">
        <DialogHeader>
          <DialogTitle>Block Dates</DialogTitle>
          <DialogDescription>
            Add one or more date ranges. Site, service, and auditors are shared across all ranges.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-60 overflow-y-auto pr-1">
          {rows.map((row, idx) => (
            <div key={row.id}>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Start *</Label>
                  <Input
                    type="date"
                    value={row.start}
                    onChange={e => updateRow(row.id, 'start', e.target.value)}
                    data-testid={`bb-start-${idx}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End</Label>
                  <Input
                    type="date"
                    value={row.end}
                    min={row.start || undefined}
                    onChange={e => updateRow(row.id, 'end', e.target.value)}
                    data-testid={`bb-end-${idx}`}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 mt-5"
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length === 1}
                  data-testid={`bb-remove-${idx}`}
                  aria-label="Remove row"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {rowErrors[row.id] && (
                <p
                  className="text-xs text-red-600 mt-1"
                  data-testid={`bb-date-error-${idx}`}
                >
                  {rowErrors[row.id]}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            data-testid="bb-add-row-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add date range
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              data-testid="bb-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="bb-save-btn"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function SchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canManageJobs, canCompleteJobs, user, isAuditor, isAdmin, isSeniorScheduler, isScheduler: isSchedulerCtx } = useAuth();
  const { isOnline, isOfflineMode, updatePendingCount, pendingCount } = useOffline();
  const { region, currencySymbol, formatCurrency } = useSettings();
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [sites, setSites] = useState([]);
  const [services, setServices] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [siteCustomerFilter, setSiteCustomerFilter] = useState('all'); // Combined filter: 'all', 'customer:id', or 'site:id'
  const [auditorFilter, setAuditorFilter] = useState('');
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [regionId, setRegionId] = useState(null);
  // Job type filter: 'JOB' (CLIENT_JOB only, default), 'ALL', 'NON_JOB' (LEAVE_* only)
  const [jobTypeFilter, setJobTypeFilter] = useState('JOB');
  
  // Parse combined filter into separate values for API
  const siteFilter = siteCustomerFilter?.startsWith('site:') ? siteCustomerFilter.split(':')[1] : '';
  const clientFilter = siteCustomerFilter?.startsWith('customer:') ? siteCustomerFilter.split(':')[1] : '';
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  // iter431 — duplicate-job confirm dialog state, used when the
  // proposal-mode create surfaces a 409 DUPLICATE_JOB_EXISTS from
  // /batch-schedule/job. Holds { detail, retry } so the user can opt
  // to "Add anyway".
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  // iter419e — Per-scope edits stay local until the user presses the
  // main "Update" button. Map of `${scope}` → { completed_on, status, auditor_ids, dirty }.
  const [scopeDrafts, setScopeDrafts] = useState({});

  // Re-seed scopeDrafts whenever the dialog opens a different job.
  React.useEffect(() => {
    if (!selectedJob || !Array.isArray(selectedJob.scope_completions)) {
      setScopeDrafts({});
      return;
    }
    const seed = {};
    for (const sc of selectedJob.scope_completions) {
      seed[sc.scope] = {
        completed_on: sc.completed_on || '',
        status: sc.status || 'scheduled',
        auditor_ids: sc.auditor_ids || [],
        delay_justified: sc.delay_justified || false,
        delay_justification_note: sc.delay_justification_note || '',
        dirty: false,
      };
    }
    setScopeDrafts(seed);
  }, [selectedJob?.id]);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [copyToDate, setCopyToDate] = useState('');

  // Block Booking (new entry only — not edit mode)
  // blockPairs: saved pair array returned from BlockBookingDialog
  // blockPairStatuses: per-pair submit tracking (keyed by pair.id)
  // blockGroupKey: ONE UUID per BB that stays identical across every pair and
  //   every retry; the backend uses it to key the dedicated DRAFT BB batch so
  //   a second BB (or any ordinary job) cannot land in the same batch.  Null
  //   until the user saves the first pair; cleared when the block is reset.
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockPairs, setBlockPairs] = useState([]);
  const [blockPairStatuses, setBlockPairStatuses] = useState({});
  const [blockGroupKey, setBlockGroupKey] = useState(null);

  // Notes dialog for auditors
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedJobForNotes, setSelectedJobForNotes] = useState(null);
  const [notesInput, setNotesInput] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Completion dialog (requires explicit completion date)
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [jobToComplete, setJobToComplete] = useState(null);
  const [completedOnDate, setCompletedOnDate] = useState('');
  const [delayJustified, setDelayJustified] = useState(false);
  const [delayJustificationNote, setDelayJustificationNote] = useState('');
  
  // Warning dialog for editing/deleting completed jobs
  const [completedJobWarningOpen, setCompletedJobWarningOpen] = useState(false);
  const [completedJobAction, setCompletedJobAction] = useState(null); // 'edit' or 'delete'
  const [pendingJob, setPendingJob] = useState(null);
  
  // Notification states
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [apDialogOpen, setApDialogOpen] = useState(false); // Approve & Publish dialog
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [publishPreview, setPublishPreview] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(null);
  // Default to batch_5pm, restore from localStorage if available
  const [sendMode, setSendMode] = useState(() => {
    const saved = localStorage.getItem('notificationSendMode');
    return saved || 'batch_5pm';
  });
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [checkingChanges, setCheckingChanges] = useState(false);
  const [changesDetected, setChangesDetected] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  // Publish filtering - selected auditors to send to
  const [selectedAuditorIds, setSelectedAuditorIds] = useState([]);
  // Publish filter options
  const [publishFilterUnnotifiedOnly, setPublishFilterUnnotifiedOnly] = useState(false);
  const [publishFilterChangedOnly, setPublishFilterChangedOnly] = useState(false);
  // Phase 1A Item 5: Client publication options
  const [useCustomEmailScript, setUseCustomEmailScript] = useState(false);
  const [customEmailScript, setCustomEmailScript] = useState('');
  const [defaultEmailScript, setDefaultEmailScript] = useState('');
  
  // Confirmation dialog state for reviewing notifications before sending
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationRecipients, setConfirmationRecipients] = useState({ auditors: [], clients: [] });
  
  // Publish dialog active tab
  const [publishTab, setPublishTab] = useState('auditors');
  
  // Phase 2A + iter467: Schedule draft workflow state
  // Everyone EXCEPT pure Admins (admin without senior_scheduler) proposes.
  // A user with the Senior Scheduler role — with or without Admin — must
  // draft and click Publish. Regular Schedulers/Auditors are unchanged.
  const isSchedulerRole = isSchedulerCtx && !isSeniorScheduler;
  const isPureAdmin = isAdmin && !isSeniorScheduler;
  const isProposalMode = (isSchedulerCtx || isAuditor || isSeniorScheduler) && !isPureAdmin;
  const [myProposals, setMyProposals] = useState([]);
  const [draftBatches, setDraftBatches] = useState([]);
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [publishingBatch, setPublishingBatch] = useState(false);
  // Approved-but-unpublished batches (visible to Admin/Senior Scheduler only)
  const [approvedUnpublishedBatches, setApprovedUnpublishedBatches] = useState([]);
  // C1: approved-awaiting-publication panel collapsed by default.
  // Count remains visible in the collapsed header; no publication action on toggle.
  const [pubPanelOpen, setPubPanelOpen] = useState(false);
  
  // Client publication filters
  const [clientFilterCustomer, setClientFilterCustomer] = useState('all');
  const [clientFilterAuditor, setClientFilterAuditor] = useState('all');
  const [clientFilterPreviouslyPublished, setClientFilterPreviouslyPublished] = useState('all'); // 'all', 'published', 'not_published'
  const [clientFilterChangedSincePublish, setClientFilterChangedSincePublish] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);  // Track selected sites for client publication
  const [clientSiteSearchQuery, setClientSiteSearchQuery] = useState('');  // Search query for customer/site filter
  
  // Client preview data - contains only sites with jobs in the date range
  const [clientPreviewData, setClientPreviewData] = useState(null);
  const [loadingClientPreview, setLoadingClientPreview] = useState(false);
  
  // Computed client contact count for display
  const [clientContactCount, setClientContactCount] = useState(0);
  
  // Pricing lines state
  const [formPricingLines, setFormPricingLines] = useState([]);  // Pricing lines for current job form
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricingLine, setEditingPricingLine] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    service_id: '',
    price: '',
    currency: region === 'UAE' ? 'AED' : 'GBP',
    description: ''
  });

  // Calculate total forecast from pricing lines
  const formForecastTotal = useMemo(() => {
    return formPricingLines.reduce((sum, line) => sum + (parseFloat(line.price) || 0), 0);
  }, [formPricingLines]);

  // Get selected site info for display
  const selectedSite = useMemo(() => {
    if (!formData.site_id) return null;
    return sites.find(s => s.id.toString() === formData.site_id.toString());
  }, [formData.site_id, sites]);

  // Check email configuration status on mount
  useEffect(() => {
    const checkEmailStatus = async () => {
      try {
        const status = await notificationsService.getStatus();
        setEmailConfigured(status.email_configured);
      } catch (err) {
        console.error('Failed to check email status:', err);
        setEmailConfigured(false);
      }
    };
    if (canManageJobs) {
      checkEmailStatus();
    }
  }, [canManageJobs, isAuditor]);
  
  // Save send mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('notificationSendMode', sendMode);
  }, [sendMode]);
  
  // Load pricing lines when editing an existing job
  const loadJobPricingLines = useCallback(async (jobId) => {
    if (!jobId) {
      setFormPricingLines([]);
      return;
    }
    setLoadingPricing(true);
    try {
      const response = await api.get(`/batch-schedule/job/${jobId}/pricing-lines`);
      setFormPricingLines(response.data.pricing_lines || []);
    } catch (err) {
      console.error('Failed to load job pricing lines:', err);
      setFormPricingLines([]);
    } finally {
      setLoadingPricing(false);
    }
  }, []);
  
  // Load default pricing lines from site when creating new job or site changes
  const loadSiteDefaultPricing = useCallback(async (siteId) => {
    if (!siteId) {
      setFormPricingLines([]);
      return;
    }
    setLoadingPricing(true);
    try {
      const lines = await sitePricingService.getForSite(siteId);
      // Filter to defaults only and format for form
      const defaults = (lines || [])
        .filter(l => l.is_default)
        .map(l => ({
          id: null, // New line, will be created on job save
          service_id: l.service_id,
          service_name: l.service_name,
          price: l.price,
          currency: l.currency,
          description: l.description,
          isNew: true  // Mark as new line to be created
        }));
      setFormPricingLines(defaults);
    } catch (err) {
      console.error('Failed to load site default pricing:', err);
      setFormPricingLines([]);
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // If offline and auditor, load from cache
      if (!isOnline && isAuditor) {
        const [cachedJobs, cachedSites, cachedServices, cachedClients] = await Promise.all([
          offlineService.getCachedJobs(),
          offlineService.getCachedSites(),
          offlineService.getCachedServices(),
          offlineService.getCachedClients()
        ]);
        
        // Apply date filter to cached jobs
        let filteredJobs = cachedJobs;
        if (startDate) {
          filteredJobs = filteredJobs.filter(job => job.scheduled_date >= startDate);
        }
        if (endDate) {
          filteredJobs = filteredJobs.filter(job => job.scheduled_date <= endDate);
        }
        
        setJobs(filteredJobs.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)));
        setSites(cachedSites);
        setServices(cachedServices);
        setClients(cachedClients);
        setAuditors([]); // Not needed for auditor view
        return;
      }
      
      const params = {
        search: search || undefined,
        status: statusFilter || undefined,
        site_id: siteFilter || undefined,
        auditor_id: auditorFilter || undefined,
        client_id: clientFilter || undefined,
        region: region || undefined,  // Filter by region (UK/UAE)
        region_id: regionId != null ? regionId : undefined, // Filter by auditor region
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: 5000  // High limit to ensure all jobs in date range are loaded
      };
      
      // Auditors have limited access - use catch for non-essential lists
      const [jobsData, sitesData, servicesData, auditorsData, clientsData] = await Promise.all([
        jobsService.list(params),
        sitesService.list({ is_active: true, region: region, limit: 500 }).catch(() => []),
        servicesService.list({ is_active: true }).catch(() => []),
        auditorsService.list({ is_active: true }).catch(() => []),
        clientsService.list({ is_active: true, region: region }).catch(() => [])
      ]);
      setJobs(jobsData);
      setSites(sitesData);
      setServices(servicesData);
      setAuditors([...auditorsData].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '')));
      setClients(clientsData);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load schedule',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, siteFilter, auditorFilter, clientFilter, startDate, endDate, region, regionId, toast, isOnline, isAuditor]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // OPTIMIZATION: Local state patching instead of full reload
  // Reduces unnecessary network calls after save operations
  // iter391 — re-sort by scheduled_date so a job moved to a different
  // date lands in the correct chronological position in the table
  // (and so any derived grouping/matrix re-buckets it correctly).
  const updateJobInState = useCallback((updatedJob) => {
    setJobs(prevJobs => {
      const merged = prevJobs.map(job =>
        job.id === updatedJob.id ? { ...job, ...updatedJob } : job
      );
      return merged.sort(
        (a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)
      );
    });
  }, []);

  const addJobToState = useCallback((newJob) => {
    setJobs(prevJobs => {
      // Insert in correct position (sorted by date)
      const newJobs = [...prevJobs, newJob];
      return newJobs.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    });
  }, []);

  const removeJobFromState = useCallback((jobId) => {
    setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
  }, []);

  // Load reference data (sites, services, auditors, clients) - rarely changes
  const loadReferenceData = useCallback(async () => {
    try {
      const [sitesData, servicesData, auditorsData, clientsData] = await Promise.all([
        sitesService.list({ is_active: true, region: region, limit: 500 }).catch(() => []),
        servicesService.list({ is_active: true }).catch(() => []),
        auditorsService.list({ is_active: true }).catch(() => []),
        clientsService.list({ is_active: true, region: region }).catch(() => [])
      ]);
      setSites(sitesData);
      setServices(servicesData);
      setAuditors([...auditorsData].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '')));
      setClients(clientsData);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  }, [region]);
  
  // Phase 2A: Load user's draft proposals for Schedule workflow
  const loadMyProposals = useCallback(async () => {
    if (!isProposalMode) {
      setMyProposals([]);
      setDraftBatches([]);
      return;
    }
    try {
      const [proposalsRes, draftRes, returnedRes] = await Promise.all([
        api.get('/jobs/proposals/my-drafts'),
        // iter467 — Force `mine=true` so the Publish/Submit control only
        // ever operates on the caller's own drafts (Senior Schedulers
        // and Admins would otherwise see everyone's org drafts).
        api.get('/batch-schedule/batches', { params: { status: 'draft', mine: true } }),
        // iter475 — Also fetch RETURNED batches so the unsent-proposals
        // banner nudges the scheduler after a Senior Scheduler returns
        // work for corrections. Otherwise returned drafts silently sit.
        api.get('/batch-schedule/batches', { params: { status: 'returned', mine: true } }),
      ]);
      setMyProposals(proposalsRes.data || []);
      // Filter to only batches with jobs OR proposals
      const drafts = (draftRes.data || []).filter(b => b.job_count > 0 || b.proposal_count > 0);
      const returned = (returnedRes.data || []).filter(b => b.job_count > 0 || b.proposal_count > 0);
      setDraftBatches([...drafts, ...returned]);

      // Approved-unpublished batches: Admin / Senior Scheduler sees APPROVED batches
      // with published_at === null and can explicitly publish them.
      if (isAdmin || isSeniorScheduler) {
        try {
          const approvedRes = await api.get('/batch-schedule/batches', { params: { status: 'approved' } });
          const approvedUnpublished = (approvedRes.data || []).filter(
            b => b.published_at === null || b.published_at === undefined
          );
          setApprovedUnpublishedBatches(approvedUnpublished);
        } catch (_e) {
          setApprovedUnpublishedBatches([]);
        }
      } else {
        setApprovedUnpublishedBatches([]);
      }
    } catch (err) {
      console.error('Failed to load proposals:', err);
      setMyProposals([]);
      setDraftBatches([]);
      setApprovedUnpublishedBatches([]);
    }
  }, [isProposalMode, isAdmin, isSeniorScheduler]);
  
  // Load proposals on mount and when proposal mode changes
  useEffect(() => {
    loadMyProposals();
  }, [loadMyProposals]);
  
  // Phase 2A: Submit batch for approval
  const handleSubmitBatch = async (batchId) => {
    setSubmittingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/submit`);
      toast({
        title: 'Submitted for Approval',
        description: response.data.message || 'Your changes have been submitted for review'
      });
      await loadMyProposals();
    } catch (err) {
      toast({
        title: 'Submission Failed',
        description: getApiError(err, 'Failed to submit for approval'),
        variant: 'destructive'
      });
    } finally {
      setSubmittingBatch(false);
    }
  };

  // iter467 — One-click Publish (submit + approve) for Senior Schedulers.
  const handlePublishBatch = async (batchId) => {
    setPublishingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/publish`);
      toast({
        title: 'Published',
        description: response.data.message || 'Your changes are now visible to everyone.'
      });
      await loadMyProposals();
      await loadData();
    } catch (err) {
      toast({
        title: 'Publish Failed',
        description: getApiError(err, 'Failed to publish batch'),
        variant: 'destructive'
      });
    } finally {
      setPublishingBatch(false);
    }
  };
  
  // Cancel draft batch - deletes draft and associated jobs/proposals
  const [cancellingBatch, setCancellingBatch] = useState(false);
  const handleCancelDraft = async (batchId, batchName) => {
    if (!window.confirm(`Are you sure you want to cancel "${batchName || `Batch #${batchId}`}"?\n\nThis will permanently delete all jobs and proposals in this draft. This action cannot be undone.`)) {
      return;
    }
    setCancellingBatch(true);
    try {
      const response = await api.post(`/batch-schedule/batch/${batchId}/cancel-draft`);
      toast({
        title: 'Draft Cancelled',
        description: response.data.message || 'Draft has been cancelled and all associated jobs deleted'
      });
      await loadMyProposals();
      loadData();
    } catch (err) {
      toast({
        title: 'Cancel Failed',
        description: getApiError(err, 'Failed to cancel draft'),
        variant: 'destructive'
      });
    } finally {
      setCancellingBatch(false);
    }
  };
  
  // Preview notification email before submitting
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBatchId, setPreviewBatchId] = useState(null);
  const [previewBatchName, setPreviewBatchName] = useState('');
  const handlePreviewNotification = async (batchId, batchName) => {
    setPreviewLoading(true);
    setPreviewBatchId(batchId);
    setPreviewBatchName(batchName || `Batch #${batchId}`);
    try {
      const response = await api.get(`/batch-schedule/batch/${batchId}/preview-notification`);
      setPreviewHtml(response.data.email_html || '<p>No preview available</p>');
      setPreviewOpen(true);
    } catch (err) {
      toast({
        title: 'Preview Failed',
        description: getApiError(err, 'Failed to load notification preview'),
        variant: 'destructive'
      });
    } finally {
      setPreviewLoading(false);
    }
  };
  const handlePreviewAndSubmit = async () => {
    setPreviewOpen(false);
    if (previewBatchId) {
      await handleSubmitBatch(previewBatchId);
    }
  };

  const openCreateDialog = () => {
    setSelectedJob(null);
    const today = format(new Date(), 'yyyy-MM-dd');
    setFormData({
      ...initialFormData,
      scheduled_date: today,
      end_date: today  // Default end date to start date (most jobs are single-day)
    });
    setFormPricingLines([]);  // Clear pricing lines for new job
    setBlockPairs([]);
    setBlockPairStatuses({});
    setDialogOpen(true);
  };

  // iter360 — Auto-open Create Job dialog when arriving from Batch Entry
  // page via /schedule?action=add. The query param is consumed (cleared)
  // immediately so a manual refresh doesn't re-pop the dialog. If the
  // caller passed `&return=batch`, we remember it and bounce back to
  // /schedule/batch when the dialog closes (so users entering several
  // complex jobs from Batch view stay in Batch view).
  const [returnToBatchAfterDialog, setReturnToBatchAfterDialog] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add' && (canManageJobs || isAuditor)) {
      const shouldReturn = params.get('return') === 'batch';
      setReturnToBatchAfterDialog(shouldReturn);
      openCreateDialog();
      params.delete('action');
      params.delete('return');
      const qs = params.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, canManageJobs, isAuditor]);

  // When the Create-Job dialog closes after a "return=batch" round-trip,
  // bounce the user back to the Batch Entry matrix.
  useEffect(() => {
    if (!dialogOpen && returnToBatchAfterDialog) {
      setReturnToBatchAfterDialog(false);
      navigate('/schedule/batch');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, returnToBatchAfterDialog]);

  const openEditDialog = (job) => {
    // If job is completed or cancelled, show warning first
    if (job.status === 'completed' || job.status === 'cancelled') {
      setPendingJob(job);
      setCompletedJobAction('edit');
      setCompletedJobWarningOpen(true);
      return;
    }
    proceedWithEdit(job);
  };

  const proceedWithEdit = async (job) => {
    // For completed/cancelled jobs, always fetch fresh data from server
    // to ensure completion date and other fields are current
    let freshJob = job;
    if (job.status === 'completed' || job.status === 'cancelled') {
      try {
        const serverJob = await jobsService.get(job.id);
        if (serverJob) {
          freshJob = serverJob;
          // Also update local state to keep it in sync
          updateJobInState(serverJob);
        }
      } catch {
        // Fallback to local data if server fetch fails
      }
    }
    setSelectedJob(freshJob);
    setFormData({
      site_id: freshJob.site_id?.toString() || '',
      service_id: freshJob.service_id?.toString() || '',
      auditor_ids: freshJob.auditors?.map(a => a.id) || [],
      client_reference: freshJob.client_reference || '',
      notes: freshJob.notes || '',
      scheduled_date: freshJob.scheduled_date || '',
      end_date: freshJob.end_date || '',
      forecast_override: freshJob.forecast_override || '',
      status: freshJob.status || 'scheduled',
      completed_on: freshJob.completed_on || format(new Date(), 'yyyy-MM-dd'),
      delay_justified: freshJob.delay_justified || false,
      delay_justification_note: freshJob.delay_justification_note || '',
      event_type: freshJob.event_type || 'CLIENT_JOB'
    });
    // Load pricing lines for existing job (only for CLIENT_JOB)
    if (!freshJob.event_type?.startsWith('LEAVE')) {
      await loadJobPricingLines(freshJob.id);
    }
    setDialogOpen(true);
  };

  const openDeleteDialog = (job) => {
    // If job is completed, show warning first
    if (job.status === 'completed') {
      setPendingJob(job);
      setCompletedJobAction('delete');
      setCompletedJobWarningOpen(true);
      return;
    }
    setSelectedJob(job);
    setDeleteDialogOpen(true);
  };

  const proceedWithDelete = () => {
    if (pendingJob) {
      setSelectedJob(pendingJob);
      setDeleteDialogOpen(true);
    }
    setCompletedJobWarningOpen(false);
    setPendingJob(null);
    setCompletedJobAction(null);
  };

  const handleCompletedJobWarningConfirm = () => {
    if (completedJobAction === 'edit' && pendingJob) {
      proceedWithEdit(pendingJob);
    } else if (completedJobAction === 'delete' && pendingJob) {
      proceedWithDelete();
    }
    setCompletedJobWarningOpen(false);
    setPendingJob(null);
    setCompletedJobAction(null);
  };

  const openCopyDialog = (job) => {
    setSelectedJob(job);
    setCopyToDate(format(addDays(new Date(job.scheduled_date), 1), 'yyyy-MM-dd'));
    setCopyDialogOpen(true);
  };

  const duplicateJob = async (job) => {
    try {
      await jobsService.create({
        site_id: job.site_id,
        service_id: job.service_id,
        auditor_ids: job.auditors?.map(a => a.id) || [],
        scheduled_date: job.scheduled_date,
        end_date: job.end_date,
        client_reference: job.client_reference,
        notes: job.notes
      });
      toast({ title: 'Success', description: 'Entry duplicated' });
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to duplicate'),
        variant: 'destructive'
      });
    }
  };

  const copyJobToDate = async () => {
    if (!selectedJob || !copyToDate) return;
    try {
      // Calculate duration if it's a multi-day job
      let endDate = null;
      if (selectedJob.end_date) {
        const startDiff = new Date(selectedJob.end_date) - new Date(selectedJob.scheduled_date);
        const daysToAdd = Math.floor(startDiff / (1000 * 60 * 60 * 24));
        endDate = format(addDays(parseISO(copyToDate), daysToAdd), 'yyyy-MM-dd');
      }
      
      await jobsService.create({
        site_id: selectedJob.site_id,
        service_id: selectedJob.service_id,
        auditor_ids: selectedJob.auditors?.map(a => a.id) || [],
        scheduled_date: copyToDate,
        end_date: endDate,
        client_reference: selectedJob.client_reference,
        notes: selectedJob.notes
      });
      toast({ title: 'Success', description: `Copied to ${format(parseISO(copyToDate), 'd MMM yyyy')}` });
      setCopyDialogOpen(false);
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to copy'),
        variant: 'destructive'
      });
    }
  };

  // ─── Block Booking submit ───────────────────────────────────────────────────
  // Called by handleSubmit when blockPairs.length > 0 (new entry only).
  // Submits each pair sequentially via the existing /batch-schedule/job endpoint,
  // preserving per-pair status across retries (success pairs are never re-sent).
  //
  // ── Block Booking idempotency ────────────────────────────────────────────────
  // Each pair carries a requestIdempotencyKey (UUID generated once on first save
  // in the parent onSave callback; preserved on reopen/retry — never regenerated
  // for an unchanged pair).
  //
  // The key is sent as `request_idempotency_key` in the payload.  The backend
  // checks this key BEFORE the duplicate-job guard and returns the committed job
  // (200) on a match — handling lost responses server-side.
  // client_reference (formData.client_reference) is the user-entered business
  // reference; it is preserved independently on every job in the block.
  //
  //   Lost response: retry carries same key → backend returns same job ID → 200.
  //   Real duplicate: different key, same site/date → 409 → confirmation dialog.
  //   Confirmed duplicate: confirm_duplicate=true → creates a 2nd job deliberately.
  const handleBlockSubmit = async () => {
    if (!formData.site_id) {
      toast({ title: 'Error', description: 'Please select a site', variant: 'destructive' });
      setSaving(false);
      return;
    }

    const batchBase = {
      site_id:     parseInt(formData.site_id),
      service_id:  formPricingLines.length > 0
        ? formPricingLines[0].service_id
        : parseInt(formData.service_id),
      auditor_ids: formData.auditor_ids,
      event_type:  'CLIENT_JOB',
      notes:       formData.notes || null,
      // client_reference is the user-entered business reference — shared across all
      // pairs in this block booking, stored independently of the idempotency key.
      client_reference: formData.client_reference || null,
    };

    // Local tracker to avoid stale-state reads inside the async loop
    const localStatuses = {};
    for (const p of blockPairs) {
      localStatuses[p.id] = blockPairStatuses[p.id] || { status: 'pending', jobId: null, errorMsg: null };
    }

    let hadDuplicate = false;
    // ── BB coherent batch: share one DRAFT batch across all pairs ────────────
    // After the first pair is successfully created, capture its schedule_batch_id
    // and pass it to every subsequent pair. This ensures the approver reviews
    // the whole booking as a single unit rather than month-fragmented batches.
    // Ensure a group key exists before submit — guarantees the backend creates
    // a dedicated BB batch tagged with THIS block's identity, so a second BB
    // (or any ordinary job) cannot be routed into the same batch.
    let effectiveGroupKey = blockGroupKey;
    if (!effectiveGroupKey) {
      effectiveGroupKey = crypto.randomUUID();
      setBlockGroupKey(effectiveGroupKey);
    }
    let bbBatchId = null;

    for (const pair of blockPairs) {
      if (localStatuses[pair.id]?.status === 'success') continue; // never retry confirmed

      localStatuses[pair.id] = { status: 'submitting', jobId: null, errorMsg: null };
      setBlockPairStatuses({ ...localStatuses });

      const payload = buildBlockBookingPayload(pair, batchBase, bbBatchId, effectiveGroupKey);

      try {
        const resp = await api.post('/batch-schedule/job', payload);
        const jobId = resp.data?.id;
        // Capture batch ID from first successful pair so subsequent pairs join
        // the same DRAFT batch (coherent per-booking approval).
        if (!bbBatchId && resp.data?.schedule_batch_id) {
          bbBatchId = resp.data.schedule_batch_id;
        }
        localStatuses[pair.id] = { status: 'success', jobId };
        setBlockPairStatuses({ ...localStatuses });
        if (jobId) {
          try {
            const newJob = await jobsService.get(jobId);
            if (newJob) addJobToState(newJob);
          } catch { /* best-effort */ }
        }
      } catch (err) {
        const httpStatus = err?.response?.status;
        const detail     = err?.response?.data?.detail;

        if (httpStatus === 409 && detail && typeof detail === 'object' && detail.code === 'DUPLICATE_JOB_EXISTS') {
          // ── Idempotency check via request_idempotency_key (defensive) ────────
          // The new backend handles lost-response retries server-side (200 replay).
          // This branch is a graceful-degradation path for edge cases where the
          // 409 still fires (e.g. concurrent timing) with our key in existing_jobs.
          const existingJobs = detail.existing_jobs || [];
          const byKey = pair.requestIdempotencyKey
            ? existingJobs.find(j => j.request_idempotency_key === pair.requestIdempotencyKey)
            : undefined;
          if (byKey) {
            localStatuses[pair.id] = { status: 'success', jobId: byKey.id };
            setBlockPairStatuses({ ...localStatuses });
            try { const j = await jobsService.get(byKey.id); if (j) addJobToState(j); } catch {}
            continue;
          }

          // ── Real duplicate — surface confirmation dialog, pause loop ───────
          // (idempotencyKey not present in any existing_jobs entry →
          //  this is a distinct job the user should decide about)
          hadDuplicate = true;
          localStatuses[pair.id] = { status: 'dup_confirm', jobId: null, errorMsg: null };
          setBlockPairStatuses({ ...localStatuses });
          const currentPair    = pair;
          const currentPayload = payload; // already carries request_idempotency_key
          setSaving(false);
          setDuplicateConfirm({
            detail,
            retry: async () => {
              setSaving(true);
              try {
                // The confirm payload carries idempotencyKey.  If this response
                // is lost, the next re-submit finds the confirm job via
                // client_reference → success without creating a third record.
                const retryResp = await api.post('/batch-schedule/job', {
                  ...currentPayload,
                  confirm_duplicate: true,
                });
                const retryId = retryResp.data?.id;
                setBlockPairStatuses(prev => ({
                  ...prev,
                  [currentPair.id]: { status: 'success', jobId: retryId },
                }));
                if (retryId) {
                  try { const j = await jobsService.get(retryId); if (j) addJobToState(j); } catch {}
                }
              } catch (retryErr) {
                setBlockPairStatuses(prev => ({
                  ...prev,
                  [currentPair.id]: { status: 'error', jobId: null, errorMsg: getApiError(retryErr, 'Failed to save') },
                }));
              } finally {
                setSaving(false);
              }
            },
          });
          break; // user must confirm before continuing
        }

        // ── Non-duplicate error: record, continue to next pair ─────────────
        localStatuses[pair.id] = { status: 'error', jobId: null, errorMsg: getApiError(err, 'Failed to schedule') };
        setBlockPairStatuses({ ...localStatuses });
      }
    }

    if (hadDuplicate) return; // setSaving(false) already called above

    setSaving(false);

    const successCount = blockPairs.filter(p => localStatuses[p.id]?.status === 'success').length;
    const allDone      = successCount === blockPairs.length;

    if (allDone) {
      toast({
        title: 'Block Schedule Created',
        description: `${successCount} date range${successCount !== 1 ? 's' : ''} scheduled successfully`,
      });
      setDialogOpen(false);
      setBlockPairs([]);
      setBlockPairStatuses({});
      setFormData(initialFormData);
      setFormPricingLines([]);
      await loadMyProposals();
    } else if (successCount > 0) {
      toast({
        title: 'Partial Success',
        description: `${successCount} of ${blockPairs.length} date ranges scheduled. Fix errors below.`,
      });
    } else {
      toast({ title: 'Submit Failed', description: 'No date ranges were scheduled.', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // ── Block Booking mode ─────────────────────────────────────────────────
    if (!selectedJob && blockPairs.length > 0) {
      await handleBlockSubmit();
      return;
    }

    try {
      if (!formData.scheduled_date) {
        toast({
          title: 'Error',
          description: 'Please select a start date',
          variant: 'destructive'
        });
        setSaving(false);
        return;
      }

      // Validate end date is not before start date
      if (formData.end_date && formData.end_date < formData.scheduled_date) {
        toast({
          title: 'Error',
          description: 'End date cannot be before start date',
          variant: 'destructive'
        });
        setSaving(false);
        return;
      }

      // Leave event update (simplified)
      if (formData.event_type?.startsWith('LEAVE') && selectedJob) {
        const leaveData = {
          scheduled_date: formData.scheduled_date,
          end_date: formData.end_date || null,
          notes: formData.notes || null,
          event_type: formData.event_type
        };
        await jobsService.update(selectedJob.id, leaveData);
        toast({
          title: 'Success',
          description: 'Leave entry updated',
        });
        setDialogOpen(false);
        setSelectedJob(null);
        setFormData(initialFormData);
        loadData();
        setSaving(false);
        return;
      }

      // Validate completed status requires auditors (for client jobs only)
      if (formData.status === 'completed' && formData.auditor_ids.length === 0) {
        toast({
          title: 'Error',
          description: 'Cannot mark as completed without assigned auditors',
          variant: 'destructive'
        });
        setSaving(false);
        return;
      }

      // Validate completion date when setting status to completed
      if (formData.status === 'completed' && !formData.completed_on) {
        toast({
          title: 'Error',
          description: 'Please enter a completion date',
          variant: 'destructive'
        });
        setSaving(false);
        return;
      }

      // Validate delay justification note when flag is set
      if (formData.status === 'completed' && formData.delay_justified) {
        if (!formData.delay_justification_note || formData.delay_justification_note.trim().length < 5) {
          toast({
            title: 'Error',
            description: 'Delay justification note is required (minimum 5 characters)',
            variant: 'destructive'
          });
          setSaving(false);
          return;
        }
      }

      const jobData = {
        site_id: parseInt(formData.site_id),
        // service_id: pricing lines take priority; fall back to user's form selection
        service_id: formPricingLines.length > 0 
          ? formPricingLines[0].service_id 
          : parseInt(formData.service_id),
        auditor_ids: formData.auditor_ids,
        client_reference: formData.client_reference || null,
        notes: formData.notes || null,
        scheduled_date: formData.scheduled_date,
        end_date: formData.end_date || null,  // null for single-day jobs
        forecast_override: formForecastTotal > 0 ? formForecastTotal : null  // Use pricing lines total
      };
      
      let jobId;
      if (selectedJob) {
        // iter473 — Per-scope completion flush BEFORE the propose-edit
        // early-return so Senior Schedulers (who otherwise return early
        // via the proposal branch) can still complete scopes directly.
        // Pure Admins keep their existing legacy flush at line ~1076
        // (they never hit isProposalMode so this guard is skipped and
        // they issue exactly one PATCH per dirty scope).
        if (isSeniorScheduler && isProposalMode && Object.keys(scopeDrafts).length > 0) {
          const dirtyEntries = Object.entries(scopeDrafts).filter(([, d]) => d.dirty);
          let successCount = 0;
          for (const [scope, draft] of dirtyEntries) {
            try {
              await api.patch(`/jobs/${selectedJob.id}/scope-completion/${scope.toLowerCase()}`, {
                status: draft.status,
                completed_on: draft.completed_on || null,
                auditor_ids: draft.auditor_ids,
                delay_justified: draft.delay_justified || false,
                delay_justification_note: draft.delay_justification_note || null,
              });
              successCount += 1;
              setScopeDrafts(prev => ({ ...prev, [scope]: { ...(prev[scope] || draft), dirty: false } }));
            } catch (err) {
              toast({
                title: `${scope === 'FOOD' ? 'Food' : 'Beverage'} scope failed to save`,
                description: getApiError(err, 'Re-open the job and try again'),
                variant: 'destructive',
              });
            }
          }
          if (successCount > 0) {
            toast({ title: 'Per-scope completion saved', description: `${successCount} scope${successCount === 1 ? '' : 's'} updated` });
          }
        }

        // Phase 2A: Proposal mode - Schedulers/Auditors/Senior Schedulers propose edits instead of direct updates
        // All jobs in the Schedule view are approved (filter applied in backend), so ANY edit
        // by a Scheduler/Auditor/SS should create a proposal.
        // iter476 — Only send fields that ACTUALLY changed compared to
        // the selectedJob. Sending unchanged values was causing the
        // notification preview to show spurious "Date changed from X
        // to X" / "Auditors changed from [A,B,C] to [A,B,C]" diffs.
        if (isProposalMode) {
          const dateStr = (v) => (v ? String(v).slice(0, 10) : null);
          const sameArr = (a, b) => {
            const A = Array.isArray(a) ? [...a].sort() : [];
            const B = Array.isArray(b) ? [...b].sort() : [];
            if (A.length !== B.length) return false;
            return A.every((x, i) => String(x) === String(B[i]));
          };
          const originalAuditorIds = Array.isArray(selectedJob.auditor_ids)
            ? selectedJob.auditor_ids
            : (selectedJob.auditors || []).map(a => a?.id).filter(Boolean);
          const originalServiceId = formPricingLines.length > 0
            ? formPricingLines[0].service_id
            : selectedJob.service_id;
          const nextServiceId = formPricingLines.length > 0
            ? formPricingLines[0].service_id
            : parseInt(formData.service_id);

          const proposedChanges = {};
          if (dateStr(formData.scheduled_date) !== dateStr(selectedJob.scheduled_date)) {
            proposedChanges.scheduled_date = formData.scheduled_date;
          }
          if (dateStr(formData.end_date || null) !== dateStr(selectedJob.end_date || null)) {
            proposedChanges.end_date = formData.end_date || null;
          }
          if (!sameArr(formData.auditor_ids, originalAuditorIds)) {
            proposedChanges.auditor_ids = formData.auditor_ids;
          }
          if (parseInt(formData.site_id) !== selectedJob.site_id) {
            proposedChanges.site_id = parseInt(formData.site_id);
          }
          if (parseInt(nextServiceId) !== parseInt(originalServiceId)) {
            proposedChanges.service_id = parseInt(nextServiceId);
          }
          if ((formData.notes || null) !== (selectedJob.notes || null)) {
            proposedChanges.notes = formData.notes || null;
          }
          if ((formData.client_reference || null) !== (selectedJob.client_reference || null)) {
            proposedChanges.client_reference = formData.client_reference || null;
          }

          if (Object.keys(proposedChanges).length === 0) {
            toast({ title: 'No changes', description: 'Nothing to propose — no fields have changed.' });
            setDialogOpen(false);
            setSelectedJob(null);
            setFormData(initialFormData);
            setSaving(false);
            return;
          }

          await api.post(`/jobs/${selectedJob.id}/propose-edit`, proposedChanges);
          toast({
            title: 'Edit Proposed',
            description: isSeniorScheduler
              ? 'Saved as draft. Publish when ready to release to everyone.'
              : 'Your changes have been saved as a draft proposal for review.'
          });
          setDialogOpen(false);
          setSelectedJob(null);
          setFormData(initialFormData);
          await loadMyProposals();
          setSaving(false);
          return;
        }
        
        // Direct update (Admin/Senior Scheduler only)
        // iter391 — capture the server response (now includes refreshed
        // assignments after a date change) so we can apply it to local
        // state below instead of patching with a constructed object
        // that omits assignments/job_dates.
        var serverUpdatedJob = await jobsService.update(selectedJob.id, jobData);
        jobId = selectedJob.id;
        
        // Handle status change OR completion date change separately
        const statusChanged = formData.status !== selectedJob.status;
        const completionDateChanged = formData.status === 'completed' && selectedJob.status === 'completed' 
          && formData.completed_on !== selectedJob.completed_on;
        // iter419g.2 — When per-scope completion is active, the manual
        // Status dropdown is locked (auto-derived from scope rows). Skip
        // the status PATCH entirely so we don't 400 against the backend
        // guard.
        const hasScopeRows = isPerScopeJob(selectedJob);
        if ((statusChanged || completionDateChanged) && !hasScopeRows) {
          // Pass completed_on and delay justification when marking as completed
          const completedOn = formData.status === 'completed' ? formData.completed_on : null;
          const delayJustified = formData.status === 'completed' ? formData.delay_justified : false;
          const delayNote = formData.status === 'completed' && formData.delay_justified 
            ? formData.delay_justification_note.trim() 
            : null;
          await jobsService.updateStatus(selectedJob.id, formData.status, completedOn, delayJustified, delayNote);
        }
        
        // Save pricing lines for existing job
        let pricingLinesOk = true;
        if (formPricingLines.length > 0 || selectedJob) {
          pricingLinesOk = await savePricingLinesToJob(jobId);
        }
        
        if (pricingLinesOk) {
          toast({ title: 'Success', description: 'Schedule updated' });
        }
      } else {
        // Phase 2A: Proposal mode - create job in draft batch
        if (isProposalMode) {
          // Use batch-schedule endpoint which creates draft batch automatically
          const batchJobData = {
            site_id: parseInt(formData.site_id),
            service_id: formPricingLines.length > 0 
              ? formPricingLines[0].service_id 
              : parseInt(formData.service_id),
            auditor_ids: formData.auditor_ids,
            scheduled_date: formData.scheduled_date,
            end_date: formData.end_date || null,
            event_type: 'CLIENT_JOB',
            notes: formData.notes || null,
            client_reference: formData.client_reference || null
          };

          // iter431 — wrap the create in its own try so we can intercept
          // the 409 DUPLICATE_JOB_EXISTS conflict and surface a
          // confirmation dialog instead of a generic error toast.
          const submitCreate = async (confirmDup) => {
            const payload = confirmDup
              ? { ...batchJobData, confirm_duplicate: true }
              : batchJobData;
            return api.post('/batch-schedule/job', payload);
          };

          try {
            await submitCreate(false);
          } catch (createErr) {
            const detail = createErr?.response?.data?.detail;
            if (
              createErr?.response?.status === 409 &&
              detail && typeof detail === 'object' &&
              detail.code === 'DUPLICATE_JOB_EXISTS'
            ) {
              setSaving(false);
              setDuplicateConfirm({
                detail,
                retry: async () => {
                  setSaving(true);
                  try {
                    await submitCreate(true);
                    toast({
                      title: 'Job Proposed',
                      description: 'New job saved as draft proposal for review',
                    });
                    setDialogOpen(false);
                    setFormData(initialFormData);
                    setFormPricingLines([]);
                    await loadMyProposals();
                  } catch (retryErr) {
                    toast({
                      title: 'Error',
                      description: getApiError(retryErr, 'Failed to save'),
                      variant: 'destructive',
                    });
                  } finally {
                    setSaving(false);
                  }
                },
              });
              return;
            }
            throw createErr;
          }

          toast({
            title: 'Job Proposed',
            description: 'New job saved as draft proposal for review'
          });
          setDialogOpen(false);
          setFormData(initialFormData);
          setFormPricingLines([]);
          await loadMyProposals();
          setSaving(false);
          return;
        }
        
        // Direct create (Admin/Senior Scheduler)
        // iter431 — wrap to catch 409 DUPLICATE_JOB_EXISTS and surface
        // the same confirmation dialog used by proposal-mode creation.
        const directCreate = async (confirmDup) => {
          const payload = confirmDup ? { ...jobData, confirm_duplicate: true } : jobData;
          return jobsService.create(payload);
        };
        let response;
        try {
          response = await directCreate(false);
        } catch (createErr) {
          const detail = createErr?.response?.data?.detail;
          if (
            createErr?.response?.status === 409 &&
            detail && typeof detail === 'object' &&
            detail.code === 'DUPLICATE_JOB_EXISTS'
          ) {
            setSaving(false);
            setDuplicateConfirm({
              detail,
              retry: async () => {
                setSaving(true);
                try {
                  const retryResp = await directCreate(true);
                  const retryJobId = retryResp?.id;
                  if (retryJobId && formPricingLines.length > 0) {
                    await savePricingLinesToJob(retryJobId);
                  }
                  toast({ title: 'Success', description: 'Schedule entry created' });
                  setDialogOpen(false);
                  setFormData(initialFormData);
                  setFormPricingLines([]);
                  try {
                    if (retryJobId) {
                      const newJob = await jobsService.get(retryJobId);
                      if (newJob) addJobToState(newJob);
                    }
                  } catch { loadData(); }
                } catch (retryErr) {
                  toast({
                    title: 'Error',
                    description: getApiError(retryErr, 'Failed to save'),
                    variant: 'destructive',
                  });
                } finally {
                  setSaving(false);
                }
              },
            });
            return;
          }
          throw createErr;
        }
        jobId = response?.id;
        
        // If user modified pricing lines before save, update them
        // Note: Backend snapshots default pricing on create, but user might have modified them
        let pricingLinesOk = true;
        if (jobId && formPricingLines.length > 0) {
          pricingLinesOk = await savePricingLinesToJob(jobId);
        }
        
        const isMultiDay = formData.end_date && formData.end_date !== formData.scheduled_date;
        if (pricingLinesOk) {
          toast({ 
            title: 'Success', 
            description: isMultiDay 
              ? 'Multi-day job created' 
              : 'Schedule entry created'
          });
        }
      }
      // iter419e — Flush any per-scope completion drafts in one pass.
      // Each dirty scope becomes a single PATCH; failures are reported
      // but don't block closing the dialog (drafts persist if dialog
      // stays open via the optimistic toast).
      // iter419g — Track the latest parent job_status returned by the
      // PATCH responses so we can flip the local Job row to COMPLETED
      // without requiring a full page refresh.
      let latestJobStatus = null;
      let scopeFlushed = 0;
      if (selectedJob && Object.keys(scopeDrafts).length > 0) {
        const dirtyEntries = Object.entries(scopeDrafts).filter(([, d]) => d.dirty);
        let successCount = 0;
        for (const [scope, draft] of dirtyEntries) {
          try {
            const resp = await api.patch(`/jobs/${selectedJob.id}/scope-completion/${scope.toLowerCase()}`, {
              status: draft.status,
              completed_on: draft.completed_on || null,
              auditor_ids: draft.auditor_ids,
              delay_justified: draft.delay_justified || false,
              delay_justification_note: draft.delay_justification_note || null,
            });
            successCount += 1;
            if (resp?.data?.job_status) {
              latestJobStatus = resp.data.job_status;
            }
          } catch (err) {
            toast({
              title: `${scope === 'FOOD' ? 'Food' : 'Beverage'} scope failed to save`,
              description: getApiError(err, 'Re-open the job and try again'),
              variant: 'destructive',
            });
          }
        }
        // iter474 — Only count successful PATCHes so a failed scope doesn't
        // masquerade as saved in the toast or trigger an unnecessary
        // parent-job refetch.
        scopeFlushed = successCount;
        if (successCount > 0) {
          toast({ title: 'Per-scope completion saved', description: `${successCount} scope${successCount === 1 ? '' : 's'} updated` });
        }
      }
      setDialogOpen(false);
      setFormPricingLines([]);

      // iter419g — If we flushed any scope drafts, refetch the parent job
      // so the local Job row reflects any auto-rolled-up status change.
      if (selectedJob && scopeFlushed > 0) {
        try {
          const freshJob = await jobsService.get(selectedJob.id);
          if (freshJob) {
            updateJobInState(freshJob);
          } else if (latestJobStatus) {
            updateJobInState({ ...selectedJob, status: latestJobStatus });
          }
        } catch {
          if (latestJobStatus) {
            updateJobInState({ ...selectedJob, status: latestJobStatus });
          }
        }
      }
      
      // After save: refresh job data from server for completion date changes,
      // or use local state patch for simple edits
      if (selectedJob) {
        const statusChanged = formData.status !== selectedJob.status;
        const completionDateChanged = formData.status === 'completed' && selectedJob.status === 'completed' 
          && formData.completed_on !== selectedJob.completed_on;
        
        if (statusChanged || completionDateChanged) {
          // Refetch from server to ensure local state is 100% accurate
          // (completion date, delay_justified etc. are updated via separate PATCH endpoint)
          try {
            const freshJob = await jobsService.get(selectedJob.id);
            if (freshJob) updateJobInState(freshJob);
          } catch {
            // Fallback: local state patch
            updateJobInState({
              ...selectedJob,
              site_id: parseInt(formData.site_id),
              service_id: formPricingLines.length > 0 ? formPricingLines[0].service_id : selectedJob.service_id,
              status: formData.status,
              completed_on: formData.status === 'completed' ? formData.completed_on : null,
              completed_at: formData.status === 'completed' ? formData.completed_on : null,
              delay_justified: formData.status === 'completed' ? formData.delay_justified : false,
            });
          }
        } else {
          // Simple edit (no status/completion change). Refetch the job
          // so pricing-line changes saved just above (savePricingLinesToJob)
          // are reflected — e.g. adding a genuine 2nd full-audit line can
          // activate per-scope completion tracking in this same save, and
          // the pre-pricing-line-sync `serverUpdatedJob` PUT response would
          // otherwise show stale scope_completions until a page refresh.
          try {
            const freshJob = await jobsService.get(jobId);
            updateJobInState(freshJob || serverUpdatedJob);
          } catch {
          if (serverUpdatedJob && typeof serverUpdatedJob === 'object') {
            updateJobInState(serverUpdatedJob);
          } else {
            const updatedJob = {
              ...selectedJob,
              site_id: parseInt(formData.site_id),
              service_id: formPricingLines.length > 0 ? formPricingLines[0].service_id : selectedJob.service_id,
              auditor_ids: formData.auditor_ids,
              client_reference: formData.client_reference || null,
              notes: formData.notes || null,
              scheduled_date: formData.scheduled_date,
              end_date: formData.end_date || formData.scheduled_date,
              forecast_override: formForecastTotal > 0 ? formForecastTotal : null,
              status: formData.status,
              completed_on: formData.status === 'completed' ? formData.completed_on : null,
              completed_at: formData.status === 'completed' ? formData.completed_on : null,
              delay_justified: formData.status === 'completed' ? formData.delay_justified : false,
              delay_justification_note: formData.status === 'completed' && formData.delay_justified ? formData.delay_justification_note : null,
              site: sites.find(s => s.id === parseInt(formData.site_id)),
              service: services.find(s => formPricingLines[0]?.service_id === s.id),
              auditors: auditors.filter(a => formData.auditor_ids.includes(a.id)),
              // iter391 — null these so the matrix falls back to
              // scheduled_date/end_date when assignments aren't in the
              // payload (otherwise stale per-auditor dates pin the job
              // to the old column).
              assignments: null,
              job_dates: null,
            };
            updateJobInState(updatedJob);
          }
          }
        }
        setSelectedJob(null);
      } else if (jobId) {
        // For new job, do a targeted refetch of just this job to get complete data
        try {
          const newJob = await jobsService.get(jobId);
          if (newJob) addJobToState(newJob);
        } catch {
          // Fallback to full reload if single job fetch fails
          loadData();
        }
      }
    } catch (err) {
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to save'),
          variant: 'destructive'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // iter419g.3 — Capture snapshot OUTSIDE try so the catch block can
    // read scope_completions when crafting the 409 force-delete prompt.
    const deletedJobId = selectedJob.id;
    const deletedJob = selectedJob;
    try {
      
      // Phase 2A: Proposal mode - Schedulers/Auditors propose deletion instead of direct delete
      // All jobs in the Schedule view are approved, so ANY delete by a Scheduler/Auditor
      // should create a proposal
      // iter470 — Senior Schedulers (± Admin) hard-delete immediately.
      // Their delete never needs Publish/approval; only regular
      // Schedulers/Auditors propose.
      if (isProposalMode && !isSeniorScheduler) {
        await api.post(`/jobs/${deletedJobId}/propose-delete`);
        toast({ 
          title: 'Deletion Proposed', 
          description: 'Job deletion saved as draft proposal for review' 
        });
        setDeleteDialogOpen(false);
        setDialogOpen(false);
        await loadMyProposals();
        return;
      }
      
      // Direct delete (Admin/Senior Scheduler only)
      await jobsService.delete(deletedJobId);
      toast({ title: 'Success', description: 'Entry deleted' });
      setDeleteDialogOpen(false);
      // OPTIMIZATION: Remove from local state instead of full reload
      removeJobFromState(deletedJobId);
    } catch (err) {
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else if (err?.response?.status === 409 && isAdmin) {
        // iter419d — Backend refuses to cascade-delete a completed job
        // (or one with reports/evidence/sessions). Admins are allowed to
        // override via ?force=true; surface that as a confirm dialog
        // instead of silently failing.
        // iter419g.3 — When the blocker is *just* "status is 'completed'"
        // on a Per-Scope job, suggest the cleaner revert-first path so
        // the admin doesn't accidentally force-cascade reports.
        const detail = err?.response?.data?.detail || 'This job has dependent records.';
        const onlyCompletedBlocker = /status is 'completed'/.test(detail)
          && !/report\(s\)|evidence|stocktake session/.test(detail);
        const hasScopeRows = isPerScopeJob(deletedJob);
        let promptMsg;
        if (onlyCompletedBlocker && hasScopeRows) {
          promptMsg =
            `This job is currently Completed (auto-derived from Per-Scope Completion).\n\n` +
            `Recommended: cancel this dialog, open the job, click "Revert" next to each completed scope, press Update — then delete normally.\n\n` +
            `OR press OK now to FORCE-DELETE as admin (cascades any future reports/evidence). This cannot be undone.`;
        } else {
          promptMsg = `${detail}\n\nForce-delete this job and all dependent records anyway? This cannot be undone.`;
        }
        const ok = window.confirm(promptMsg);
        if (ok) {
          try {
            await api.delete(`/jobs/${deletedJobId}?force=true`);
            toast({ title: 'Deleted', description: 'Job force-deleted by admin' });
            setDeleteDialogOpen(false);
            removeJobFromState(deletedJobId);
          } catch (err2) {
            toast({
              title: 'Force delete failed',
              description: getApiError(err2, 'Try again'),
              variant: 'destructive',
            });
          }
        }
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to delete'),
          variant: 'destructive'
        });
      }
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    // Only Scheduler/Admin can mark jobs as completed
    if (newStatus === 'completed' && !canCompleteJobs) {
      toast({
        title: 'Not Allowed',
        description: 'Only Schedulers and Admins can mark jobs as completed',
        variant: 'destructive'
      });
      return;
    }

    // iter419g.2 — Per-scope jobs: manual status changes are blocked
    // by the backend; nudge the user to use the Per-Scope panel instead.
    if (isPerScopeJob(job)) {
      toast({
        title: 'Use Per-Scope Completion',
        description: 'This job uses Per-Scope Completion. Open the job and mark each scope in the Per-Scope panel — the overall status auto-flips once every scope is complete.',
      });
      return;
    }
    
    // For completion, we need to capture the completion date and delay justification
    if (newStatus === 'completed') {
      setJobToComplete(job);
      setCompletedOnDate(format(new Date(), 'yyyy-MM-dd')); // Default to today
      setDelayJustified(false);
      setDelayJustificationNote('');
      setCompletionDialogOpen(true);
      return;
    }
    
    try {
      await jobsService.updateStatus(job.id, newStatus);
      toast({ title: 'Success', description: `Marked as ${statusLabels[newStatus]}` });
      // OPTIMIZATION: Update status in local state
      updateJobInState({ ...job, status: newStatus });
    } catch (err) {
      if (isLockedError(err)) {
        showLockedToast(toast, err);
      } else {
        toast({
          title: 'Error',
          description: getApiError(err, 'Failed to update status'),
          variant: 'destructive'
        });
      }
    }
  };

  const confirmComplete = async () => {
    if (!jobToComplete || !completedOnDate) return;
    
    // Validate delay justification note if flag is set
    if (delayJustified && (!delayJustificationNote || delayJustificationNote.trim().length < 5)) {
      toast({
        title: 'Error',
        description: 'Please provide a justification note (at least 5 characters)',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      await jobsService.updateStatus(
        jobToComplete.id, 
        'completed', 
        completedOnDate,
        delayJustified,
        delayJustified ? delayJustificationNote.trim() : null
      );
      toast({ title: 'Success', description: 'Job marked as Completed' });
      setCompletionDialogOpen(false);
      setJobToComplete(null);
      setDelayJustified(false);
      setDelayJustificationNote('');
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to mark as completed'),
        variant: 'destructive'
      });
    }
  };

  // Inline editing functions - REMOVED: Replaced with full edit dialog
  // The edit button now opens the full dialog instead of inline editing

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // iter476 — When scheduled_date (start) changes, keep end_date in
      // range:
      //   * If end_date is empty, leave it (single-day / server default).
      //   * If end_date < new start, snap end_date to the new start.
      //   * If end_date was equal to the OLD start (i.e. it looked like
      //     a single-day placeholder), keep it equal to the new start so
      //     the job stays a single-day job when moved.
      // Previously we only clamped when end_date < value, so moving a
      // date EARLIER left the old end_date behind and produced
      // multi-month jobs by accident (see iteration_476 audit).
      if (field === 'scheduled_date' && updated.end_date) {
        if (prev.end_date === prev.scheduled_date) {
          updated.end_date = value;
        } else if (updated.end_date < value) {
          updated.end_date = value;
        }
      }

      // When site changes and creating new job, load default pricing
      if (field === 'site_id' && value && !selectedJob) {
        loadSiteDefaultPricing(parseInt(value));
      }

      return updated;
    });
  };
  
  // Pricing line management functions for form
  const openFormPricingDialog = (line = null, index = null) => {
    setEditingPricingLine(line ? { ...line, index } : null);
    setPricingForm({
      service_id: line?.service_id?.toString() || '',
      price: line?.price?.toString() || '',
      currency: line?.currency || (region === 'UAE' ? 'AED' : 'GBP'),
      description: line?.description || ''
    });
    setPricingDialogOpen(true);
  };
  
  const handleFormPricingLineSave = async () => {
    if (!pricingForm.service_id || !pricingForm.price) {
      toast({ title: 'Error', description: 'Service and price are required', variant: 'destructive' });
      return;
    }
    
    const selectedService = services.find(s => s.id === parseInt(pricingForm.service_id));
    const newLine = {
      service_id: parseInt(pricingForm.service_id),
      service_name: selectedService?.name || 'Unknown Service',
      price: parseFloat(pricingForm.price),
      currency: pricingForm.currency,
      description: pricingForm.description || null,
      isNew: !editingPricingLine?.id  // Mark as new if no existing ID
    };
    
    if (editingPricingLine && editingPricingLine.index !== null) {
      // Update existing line in form
      setFormPricingLines(prev => {
        const updated = [...prev];
        updated[editingPricingLine.index] = { ...updated[editingPricingLine.index], ...newLine };
        return updated;
      });
    } else {
      // Add new line to form
      setFormPricingLines(prev => [...prev, { id: null, ...newLine }]);
    }
    
    setPricingDialogOpen(false);
    toast({ title: 'Success', description: editingPricingLine ? 'Pricing line updated' : 'Pricing line added' });
  };
  
  const handleFormPricingLineDelete = (index) => {
    if (!window.confirm('Delete this pricing line?')) return;
    setFormPricingLines(prev => prev.filter((_, i) => i !== index));
  };
  
  // Save pricing lines to backend after job is created/updated
  const savePricingLinesToJob = async (jobId) => {
    // For existing jobs, we need to sync pricing lines
    // Add new lines, update existing, delete removed
    try {
      // Get current pricing lines from backend
      const response = await api.get(`/batch-schedule/job/${jobId}/pricing-lines`);
      const existingLines = response.data.pricing_lines || [];
      const existingIds = existingLines.map(l => l.id);
      const formIds = formPricingLines.filter(l => l.id).map(l => l.id);

      // Run every delete/add/update CONCURRENTLY (was a sequential
      // await-per-line loop, ~200-300ms/query x N lines — the main
      // cause of the slow ~18-20s Edit Entry save). Each endpoint
      // self-heals its own per-scope classification, but under true
      // concurrency those reads can race each other, so a single
      // authoritative reseed-scope call below settles the final state.
      const writes = [];
      for (const existingLine of existingLines) {
        if (!formIds.includes(existingLine.id)) {
          writes.push(api.delete(`/batch-schedule/job/${jobId}/pricing-lines/${existingLine.id}`));
        }
      }
      for (const formLine of formPricingLines) {
        const data = {
          service_id: formLine.service_id,
          price: formLine.price,
          currency: formLine.currency,
          description: formLine.description
        };
        if (formLine.id && existingIds.includes(formLine.id)) {
          writes.push(api.put(`/batch-schedule/job/${jobId}/pricing-lines/${formLine.id}`, data));
        } else {
          writes.push(api.post(`/batch-schedule/job/${jobId}/pricing-lines`, data));
        }
      }
      await Promise.all(writes);

      if (writes.length > 0) {
        await api.post(`/batch-schedule/job/${jobId}/pricing-lines/reseed-scope`);
      }
      return true;
    } catch (err) {
      console.error('Failed to save pricing lines:', err);
      toast({
        title: 'Pricing lines not fully saved',
        description: getApiError(err, 'Some pricing line changes may not have been saved. Please reopen this entry to check.'),
        variant: 'destructive',
      });
      // Don't throw - job itself was already saved. Caller uses the
      // return value to skip/avoid overwriting this toast with a
      // generic success one (TOAST_LIMIT=1 would otherwise hide it).
      return false;
    }
  };

  const toggleAuditor = (auditorId) => {
    setFormData(prev => ({
      ...prev,
      auditor_ids: prev.auditor_ids.includes(auditorId)
        ? prev.auditor_ids.filter(id => id !== auditorId)
        : [...prev.auditor_ids, auditorId]
    }));
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSiteCustomerFilter('all');
    setAuditorFilter('');
    setStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  };

  const hasActiveFilters = search || statusFilter || siteCustomerFilter !== 'all' || auditorFilter;

  // Client-side job-type filter applied on top of server-fetched jobs.
  // Does NOT mutate the jobs state or trigger any API call.
  const displayedJobs = useMemo(() => {
    if (jobTypeFilter === 'JOB') return jobs.filter(j => !j.event_type?.startsWith('LEAVE'));
    if (jobTypeFilter === 'NON_JOB') return jobs.filter(j => j.event_type?.startsWith('LEAVE'));
    return jobs;
  }, [jobs, jobTypeFilter]);

  // Notes dialog functions for auditors
  const openNotesDialog = (job) => {
    setSelectedJobForNotes(job);
    setNotesInput(job.notes || '');
    setNotesDialogOpen(true);
  };

  const saveNotes = async () => {
    if (!selectedJobForNotes) return;
    setSavingNotes(true);
    
    try {
      if (isOnline) {
        // Online: save directly
        await jobsService.update(selectedJobForNotes.id, {
          site_id: selectedJobForNotes.site_id,
          service_id: selectedJobForNotes.service_id,
          auditor_ids: selectedJobForNotes.auditors?.map(a => a.id) || [],
          scheduled_date: selectedJobForNotes.scheduled_date,
          client_reference: selectedJobForNotes.client_reference,
          notes: notesInput || null
        });
        toast({ title: 'Saved', description: 'Notes updated' });
      } else {
        // Offline: save to outbox
        const result = await offlineService.addNoteOffline(selectedJobForNotes.id, notesInput);
        if (result.success) {
          toast({ title: 'Saved Offline', description: 'Notes will sync when online' });
          updatePendingCount();
        } else {
          throw new Error(result.error);
        }
      }
      
      setNotesDialogOpen(false);
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.message || 'Failed to save notes',
        variant: 'destructive'
      });
    } finally {
      setSavingNotes(false);
    }
  };

  // Notification functions
  const openPublishDialog = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Select Date Range',
        description: 'Please set start and end dates before publishing',
        variant: 'destructive'
      });
      return;
    }
    
    setPublishLoading(true);
    setPublishDialogOpen(true);
    setPublishFilterUnnotifiedOnly(false);
    setPublishFilterChangedOnly(false);
    // Reset client publication options
    setUseCustomEmailScript(false);
    setSelectedSiteIds([]); // Reset selected sites
    setClientSiteSearchQuery(''); // Reset search query
    setClientFilterCustomer('all'); // Reset customer filter
    setClientFilterAuditor('all'); // Reset auditor filter
    setClientFilterPreviouslyPublished('all'); // Reset publication status filter
    setClientFilterChangedSincePublish(false); // Reset changed filter
    setClientContactCount(0); // Reset client contact count
    setClientPreviewData(null); // Reset client preview data
    
    // Load default email script from settings
    try {
      const settings = await settingsService.get();
      const script = settings?.schedule_publication_email_script || '';
      setDefaultEmailScript(script);
      setCustomEmailScript(script); // Pre-fill custom script with default
    } catch (err) {
      console.warn('Failed to load settings email script:', err);
      setDefaultEmailScript('');
      setCustomEmailScript('');
    }
    
    // Load auditor preview
    await loadPublishPreview(false, false);
    
    // Load client preview (sites with jobs in the date range)
    await loadClientPreview();
  };
  
  // Load client preview - fetches sites that actually have jobs in the date range
  const loadClientPreview = async () => {
    setLoadingClientPreview(true);
    try {
      const preview = await notificationsService.getClientPublishPreview({
        start_date: startDate,
        end_date: endDate,
        region: region || undefined
      });
      setClientPreviewData(preview);
    } catch (err) {
      console.warn('Failed to load client preview:', err);
      setClientPreviewData(null);
    } finally {
      setLoadingClientPreview(false);
    }
  };
  
  // Load publish preview with optional filters
  const loadPublishPreview = async (unnotifiedOnly = false, changedOnly = false) => {
    setPublishLoading(true);
    try {
      const preview = await notificationsService.getPreview({
        start_date: startDate,
        end_date: endDate,
        region: region || undefined,
        unnotified_only: unnotifiedOnly,
        changed_only: changedOnly
      });
      setPublishPreview(preview);
      // Select all auditors by default
      setSelectedAuditorIds(preview.auditor_details.map(a => a.auditor_id));
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load publish preview',
        variant: 'destructive'
      });
      setPublishDialogOpen(false);
    } finally {
      setPublishLoading(false);
    }
  };

  // Toggle auditor selection
  const toggleAuditorSelection = (auditorId) => {
    setSelectedAuditorIds(prev => 
      prev.includes(auditorId) 
        ? prev.filter(id => id !== auditorId)
        : [...prev, auditorId]
    );
  };

  // Select/deselect all auditors
  const toggleAllAuditors = () => {
    if (selectedAuditorIds.length === publishPreview?.auditor_details?.length) {
      setSelectedAuditorIds([]);
    } else {
      setSelectedAuditorIds(publishPreview?.auditor_details?.map(a => a.auditor_id) || []);
    }
  };

  // Toggle site selection for client publication
  const toggleSiteSelection = (siteId) => {
    setSelectedSiteIds(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  // Get filtered sites for client publication based on search and filters
  // ONLY includes sites that have jobs in the selected date range (from clientPreviewData)
  const filteredClientSites = useMemo(() => {
    const query = clientSiteSearchQuery.toLowerCase().trim();
    
    // If client preview data is available, use it (contains only sites with jobs)
    if (clientPreviewData?.site_details) {
      return clientPreviewData.site_details.filter(site => {
        // Apply customer filter
        if (clientFilterCustomer !== 'all') {
          const customerId = parseInt(clientFilterCustomer);
          if (site.customer_id !== customerId) {
            return false;
          }
        }
        
        // Apply search filter
        if (query) {
          if (!site.site_name.toLowerCase().includes(query) && 
              !site.customer_name.toLowerCase().includes(query)) {
            return false;
          }
        }
        
        return true;
      }).map(site => ({
        // Map to match the format expected by the UI
        id: site.site_id,
        name: site.site_name,
        parent_client_id: site.customer_id,
        independent_client_id: null, // Preview doesn't distinguish
        customer_name: site.customer_name,
        customer_colour: site.customer_colour,
        job_count: site.job_count,
        has_scheduling_contacts: site.has_scheduling_contacts,
        contacts: site.contacts
      }));
    }
    
    // Fallback to local sites data if client preview not available
    return sites.filter(site => {
      // Only include active sites (not deleted)
      if (site.deleted_at || site.is_active === false) {
        return false;
      }
      
      // Get customer and check if it's active
      const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
      if (!customer || customer.deleted_at || customer.is_active === false) {
        return false;
      }
      
      // Apply customer filter
      if (clientFilterCustomer !== 'all') {
        const customerId = parseInt(clientFilterCustomer);
        if (site.parent_client_id !== customerId && site.independent_client_id !== customerId) {
          return false;
        }
      }
      
      // Apply search filter
      if (query) {
        const customerName = customer?.name || '';
        if (!site.name.toLowerCase().includes(query) && !customerName.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      return true;
    }).map(site => {
      const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
      return {
        ...site,
        customer_name: customer?.name || '-',
        customer_colour: customer?.colour,
        job_count: 0,  // Unknown from local data
        has_scheduling_contacts: false,  // Unknown from local data
        contacts: []
      };
    });
  }, [clientPreviewData, sites, clients, clientFilterCustomer, clientSiteSearchQuery]);

  // Select/deselect all filtered sites
  const toggleAllSites = () => {
    const filteredSiteIds = filteredClientSites.map(s => s.id);
    const allSelected = filteredSiteIds.every(id => selectedSiteIds.includes(id));
    
    if (allSelected) {
      // Deselect all filtered sites
      setSelectedSiteIds(prev => prev.filter(id => !filteredSiteIds.includes(id)));
    } else {
      // Select all filtered sites
      setSelectedSiteIds(prev => [...new Set([...prev, ...filteredSiteIds])]);
    }
  };

  // Check if all filtered sites are selected
  const allFilteredSitesSelected = useMemo(() => {
    const filteredSiteIds = filteredClientSites.map(s => s.id);
    return filteredSiteIds.length > 0 && filteredSiteIds.every(id => selectedSiteIds.includes(id));
  }, [filteredClientSites, selectedSiteIds]);

  // Prepare confirmation dialog data - called when user clicks Send
  const prepareConfirmation = async () => {
    if (!emailConfigured) {
      toast({
        title: 'Email Not Configured',
        description: 'SendGrid API key is not configured. Please contact your administrator.',
        variant: 'destructive'
      });
      return;
    }

    // Tab-specific validation
    if (publishTab === 'auditors' && selectedAuditorIds.length === 0) {
      toast({
        title: 'No Auditors Selected',
        description: 'Please select at least one auditor to send notifications.',
        variant: 'destructive'
      });
      return;
    }

    if (publishTab === 'clients' && selectedSiteIds.length === 0) {
      toast({
        title: 'No Sites Selected',
        description: 'Please select at least one site to send client notifications.',
        variant: 'destructive'
      });
      return;
    }

    // Prepare recipients for confirmation
    let auditorRecipients = [];
    let clientRecipients = [];

    if (publishTab === 'auditors') {
      auditorRecipients = publishPreview?.auditor_details
        ?.filter(a => selectedAuditorIds.includes(a.auditor_id))
        .map(a => ({ ...a, selected: true })) || [];
    } else if (publishTab === 'clients') {
      // Fetch actual contact information from backend
      try {
        const clientPreview = await notificationsService.getClientPublishPreview({
          start_date: startDate,
          end_date: endDate,
          region: region || undefined
        });
        
        // Filter to only selected sites and add contact info
        clientRecipients = clientPreview.site_details
          .filter(s => selectedSiteIds.includes(s.site_id))
          .map(site => ({
            site_id: site.site_id,
            site_name: site.site_name,
            customer_name: site.customer_name || '-',
            customer_colour: site.customer_colour,
            contacts: site.contacts || [],
            has_scheduling_contacts: site.has_scheduling_contacts,
            job_count: site.job_count,
            selected: site.has_scheduling_contacts  // Auto-select only if has contacts
          }));
        
        // Show warning if some sites have no contacts
        const sitesWithoutContacts = clientRecipients.filter(s => !s.has_scheduling_contacts);
        if (sitesWithoutContacts.length > 0) {
          toast({
            title: 'Warning: Missing Contacts',
            description: `${sitesWithoutContacts.length} site(s) have no contacts with Schedule Publication and Billing enabled. They will not receive emails.`,
            variant: 'destructive',
            duration: 5000
          });
        }
      } catch (err) {
        console.error('Failed to fetch client preview:', err);
        // Fallback to local data if API fails
        clientRecipients = filteredClientSites
          .filter(s => selectedSiteIds.includes(s.id))
          .map(site => {
            const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
            return {
              site_id: site.id,
              site_name: site.name,
              customer_name: customer?.name || '-',
              customer_colour: customer?.colour,
              contacts: [],
              has_scheduling_contacts: false,  // Unknown
              selected: true
            };
          });
      }
    }

    setConfirmationRecipients({ auditors: auditorRecipients, clients: clientRecipients });
    setConfirmDialogOpen(true);
  };

  // Toggle recipient selection in confirmation dialog
  const toggleConfirmationRecipient = (type, id) => {
    setConfirmationRecipients(prev => ({
      ...prev,
      [type]: prev[type].map(r => 
        (type === 'auditors' ? r.auditor_id === id : r.site_id === id) 
          ? { ...r, selected: !r.selected } 
          : r
      )
    }));
  };

  // Select/deselect all in confirmation dialog
  const toggleAllConfirmationRecipients = (type) => {
    const allSelected = confirmationRecipients[type].every(r => r.selected);
    setConfirmationRecipients(prev => ({
      ...prev,
      [type]: prev[type].map(r => ({ ...r, selected: !allSelected }))
    }));
  };

  const handlePublish = async () => {
    const selectedAuditors = confirmationRecipients.auditors.filter(a => a.selected);
    const selectedClients = confirmationRecipients.clients.filter(c => c.selected);
    
    if (selectedAuditors.length === 0 && selectedClients.length === 0) {
      toast({
        title: 'No Recipients Selected',
        description: 'Please select at least one recipient.',
        variant: 'destructive'
      });
      return;
    }
    
    setSendingNotifications(true);
    setConfirmDialogOpen(false);
    
    try {
      const result = await notificationsService.publish({
        start_date: startDate,
        end_date: endDate,
        region: region || null,
        send_mode: sendMode,
        // Only send to auditors if we have auditor selections
        auditor_ids: selectedAuditors.length > 0 ? selectedAuditors.map(a => a.auditor_id) : [],
        // Client publication - send site IDs for client notifications
        include_clients: selectedClients.length > 0,
        selected_site_ids: selectedClients.length > 0 ? selectedClients.map(c => c.site_id) : [],
        custom_email_script: useCustomEmailScript && customEmailScript ? customEmailScript : null
      });
      
      setPublishDialogOpen(false);
      setPublishPreview(null);
      setSelectedAuditorIds([]);
      setSelectedSiteIds([]);
      
      const totalRecipients = (result.total_auditors || 0) + (result.total_clients || 0);
      
      if (sendMode === 'batch_5pm') {
        toast({
          title: 'Queued for 5 PM',
          description: `${totalRecipients} recipient(s) will receive notifications at 5 PM`,
        });
      } else {
        const successCount = result.send_results?.filter(r => r.success).length || 0;
        toast({
          title: 'Notifications Sent',
          description: `${successCount}/${totalRecipients} email(s) sent successfully`,
        });
      }
    } catch (err) {
      console.error('Publish error:', err);
      toast({
        title: 'Failed to Send',
        description: err.response?.data?.detail || err.message || 'Failed to send notifications',
        variant: 'destructive'
      });
    } finally {
      setSendingNotifications(false);
    }
  };

  const checkForChanges = async () => {
    if (!startDate || !endDate) return;
    
    setCheckingChanges(true);
    try {
      const changes = await notificationsService.detectChanges({
        start_date: startDate,
        end_date: endDate,
        region: region || undefined
      });
      setChangesDetected(changes);
      
      if (changes.has_changes) {
        const parts = [];
        if (changes.jobs_added > 0) parts.push(`${changes.jobs_added} new`);
        if (changes.jobs_date_changed > 0) parts.push(`${changes.jobs_date_changed} date changes`);
        if (changes.jobs_auditor_changed > 0) parts.push(`${changes.jobs_auditor_changed} auditor changes`);
        if (changes.jobs_line_item_changed > 0) parts.push(`${changes.jobs_line_item_changed} line item changes`);
        
        toast({
          title: 'Changes Detected',
          description: parts.join(', '),
        });
      } else {
        toast({
          title: 'No Changes',
          description: 'No schedule changes detected since last notification',
        });
      }
    } catch (err) {
      console.error('Failed to detect changes:', err);
    } finally {
      setCheckingChanges(false);
    }
  };

  // Mark a single job as notified
  const handleMarkJobNotified = async (jobId) => {
    try {
      const result = await notificationsService.markSingleNotified(jobId);
      toast({
        title: 'Marked as Notified',
        description: result.message || 'Job marked as already notified',
      });
      // Refresh the jobs list to update the notification indicator
      loadData();
    } catch (err) {
      console.error('Mark notified error:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.message || 'Failed to mark as notified',
        variant: 'destructive'
      });
    }
  };

  // Mark all jobs in current view as notified
  const handleMarkAllNotified = async () => {
    if (!startDate || !endDate) return;
    
    try {
      const result = await notificationsService.markNotified({
        start_date: startDate,
        end_date: endDate
      });
      toast({
        title: 'Jobs Marked',
        description: result.message || `${result.jobs_marked} jobs marked as notified`,
      });
      // Refresh the jobs list to update notification indicators
      loadData();
    } catch (err) {
      console.error('Mark all notified error:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.message || 'Failed to mark jobs',
        variant: 'destructive'
      });
    }
  };

  const handleSendUpdates = async () => {
    if (!emailConfigured) {
      toast({
        title: 'Email Not Configured',
        description: 'SendGrid API key is not configured. Please contact your administrator.',
        variant: 'destructive'
      });
      return;
    }
    
    setSendingNotifications(true);
    try {
      const result = await notificationsService.sendUpdates({
        start_date: startDate,
        end_date: endDate,
        region: region || null,
        send_mode: sendMode
      });
      
      setChangesDetected(null);
      
      if (sendMode === 'batch_5pm') {
        toast({
          title: 'Updates Queued',
          description: `Updates will be sent to ${result.total_auditors} auditors at 5 PM`,
        });
      } else {
        const successCount = result.send_results?.filter(r => r.success).length || 0;
        toast({
          title: 'Updates Sent',
          description: `${successCount}/${result.total_auditors} update emails sent`,
        });
      }
    } catch (err) {
      toast({
        title: 'Failed to Send Updates',
        description: getApiError(err, 'Failed to send update notifications'),
        variant: 'destructive'
      });
    } finally {
      setSendingNotifications(false);
    }
  };

  return (
    <div data-testid="schedule-page">
      <Toaster />

      {/* iter431 — Duplicate job confirmation dialog */}
      <DuplicateJobConfirmDialog
        open={!!duplicateConfirm}
        duplicateInfo={duplicateConfirm?.detail}
        onClose={() => setDuplicateConfirm(null)}
        onConfirm={async () => {
          const retry = duplicateConfirm?.retry;
          setDuplicateConfirm(null);
          if (retry) await retry();
        }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            {/* iter476 — Only show "My Schedule" for pure auditors. Multi-role
                users with admin/senior_scheduler get the full "Schedule"
                view because their data isn't auditor-scoped. */}
            {(isAuditor && !isAdmin && !isSeniorScheduler && !isSchedulerCtx) ? 'My Schedule' : 'Schedule'}
          </h1>
          <p className="text-muted-foreground">
            {(isAuditor && !isAdmin && !isSeniorScheduler && !isSchedulerCtx) ? 'Your assigned work' : 'Manage audit schedule'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Email Status Indicator - Pill Style */}
          {canManageJobs && emailConfigured !== null && (
            <div 
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                emailConfigured 
                  ? "bg-green-100 text-green-800 border border-green-200" 
                  : "bg-amber-100 text-amber-800 border border-amber-200"
              )}
              data-testid="email-status-pill"
            >
              <Mail className="h-3 w-3" />
              {emailConfigured ? 'Email: On' : 'Email: Off'}
              {emailConfigured ? (
                <CheckCircle className="h-3 w-3" />
              ) : (
                <AlertCircle className="h-3 w-3" />
              )}
            </div>
          )}
          
          {/* Batch Entry */}
          {canManageJobs && (
            <a href="/schedule/batch" className="inline-flex">
              <Button variant="outline" size="sm" data-testid="batch-entry-btn">
                <Grid3X3 className="h-4 w-4 mr-2" />
                Batch
              </Button>
            </a>
          )}
          
          {/* Preview & Publish Button */}
          {canManageJobs && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={openPublishDialog}
              disabled={publishLoading}
              data-testid="publish-btn"
            >
              {publishLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Publish
            </Button>
          )}

          {/* Unpublish Jobs Button — Admin / Senior Scheduler / Scheduler only */}
          {canManageJobs && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUnpublishDialogOpen(true)}
              data-testid="unpublish-jobs-btn"
              className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
            >
              <EyeOff className="h-4 w-4 mr-2" />
              Unpublish
            </Button>
          )}
          
          {/* More Actions Dropdown */}
          {canManageJobs && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="more-actions-btn">
                  <Bell className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleMarkAllNotified}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark All as Notified
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/notifications/logs" className="flex items-center">
                    <Mail className="h-4 w-4 mr-2" />
                    Notification History
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Add Entry - Schedulers/Senior Schedulers/Admins OR Auditors in proposal mode */}
          {(canManageJobs || isProposalMode) && (
            <Button onClick={openCreateDialog} data-testid="add-schedule-btn" size="sm">
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{isProposalMode ? 'Propose Entry' : 'Add Entry'}</span>
            </Button>
          )}
          
          {/* Phase 2A: Submit button for Scheduler/Auditor proposal mode */}
          {isProposalMode && draftBatches.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="default" 
                  size="sm"
                  className={isSeniorScheduler ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}
                  data-testid="submit-proposals-btn"
                >
                  <Check className="h-4 w-4 mr-1" />
                  {isSeniorScheduler ? 'Publish' : 'Submit'} ({draftBatches.reduce((sum, b) => sum + (b.job_count || 0) + (b.proposal_count || 0), 0)})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-3">
                  <h4 className="font-medium">
                    {isSeniorScheduler ? 'Publish Draft Batches' : 'Submit Changes for Approval'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {isSeniorScheduler
                      ? 'Publishing a batch submits and approves it in one click — the changes become visible to everyone.'
                      : 'Your proposed changes will be sent for Senior Scheduler review.'}
                  </p>
                  {draftBatches.map(batch => (
                    <div key={batch.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div>
                        <div className="text-sm font-medium">{batch.name || `Batch #${batch.id}`}</div>
                        <div className="text-xs text-muted-foreground">
                          {batch.job_count > 0 && `${batch.job_count} new job(s)`}
                          {batch.job_count > 0 && batch.proposal_count > 0 && ', '}
                          {batch.proposal_count > 0 && `${batch.proposal_count} change(s)`}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancelDraft(batch.id, batch.name)}
                          disabled={cancellingBatch}
                          data-testid={`cancel-draft-${batch.id}`}
                        >
                          {cancellingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreviewNotification(batch.id, batch.name)}
                          disabled={previewLoading}
                          data-testid={`preview-batch-${batch.id}`}
                        >
                          {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        {isSeniorScheduler ? (
                          // iter467 — Senior Schedulers use one-click Publish
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => handlePublishBatch(batch.id)}
                            disabled={publishingBatch}
                            data-testid={`publish-batch-${batch.id}`}
                            title="Publish — submit and approve in one click"
                          >
                            {publishingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Publish'}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleSubmitBatch(batch.id)}
                            disabled={submittingBatch}
                            data-testid={`submit-batch-${batch.id}`}
                          >
                            {submittingBatch ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Submit'}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
      
      {/* Email Not Configured Warning */}
      {canManageJobs && emailConfigured === false && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Email Notifications Not Configured</p>
                <p className="text-sm text-amber-700 mt-1">
                  SendGrid API key is not configured. Schedule publishing and update notifications are disabled until email is configured.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter475 — Unsent proposals nudge for regular Schedulers. Their
          draft batches are private until they hit Submit; without this
          banner it's easy to walk away thinking the change is already
          up for approval. Senior Schedulers use the Publish popover and
          don't need this reminder. */}
      {isSchedulerRole && draftBatches.length > 0 && (() => {
        const totalItems = draftBatches.reduce((sum, b) => sum + (b.job_count || 0) + (b.proposal_count || 0), 0);
        if (totalItems === 0) return null;
        return (
          <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="unsent-proposals-banner">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900">
                    You have {totalItems} unsent proposal{totalItems === 1 ? '' : 's'}
                  </p>
                  <p className="text-sm text-amber-800 mt-1">
                    Your changes are still sitting in {draftBatches.length === 1 ? 'a draft batch' : `${draftBatches.length} draft batches`} and haven&apos;t been sent for approval yet. Use <span className="font-semibold">Submit All for Approval</span> to notify your Senior Scheduler.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                  onClick={async () => {
                    for (const batch of draftBatches) {
                      // Sequential to make notifications deterministic
                      // and to avoid concurrent submit races.
                      // eslint-disable-next-line no-await-in-loop
                      await handleSubmitBatch(batch.id);
                    }
                  }}
                  disabled={submittingBatch}
                  data-testid="submit-all-drafts-btn"
                >
                  {submittingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Submit All for Approval
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Approved-Unpublished batches banner — Admin / Senior Scheduler only.
          These batches are APPROVED internally but NOT yet visible to auditors
          or clients. Use the combined Approve & Publish dialog to release them. */}
      {(isAdmin || isSeniorScheduler) && approvedUnpublishedBatches.length > 0 && (
        <Card className="mb-6 border-blue-300 bg-blue-50" data-testid="approved-unpublished-banner">
          {/* Always-visible collapsed header — click anywhere to toggle */}
          <button
            type="button"
            className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-t"
            onClick={() => setPubPanelOpen(open => !open)}
            aria-expanded={pubPanelOpen}
            aria-controls="pub-panel-body"
            data-testid="pub-panel-toggle"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <span className="flex-1 font-semibold text-blue-900 text-sm">
                {approvedUnpublishedBatches.length === 1
                  ? '1 approved batch is pending publication'
                  : `${approvedUnpublishedBatches.length} approved batches are pending publication`}
              </span>
              <Button
                size="sm"
                variant="default"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-3 flex-shrink-0"
                onClick={e => { e.stopPropagation(); setApDialogOpen(true); }}
                data-testid="schedule-approve-publish-btn"
              >
                <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
                Approve &amp; Publish
              </Button>
              {pubPanelOpen
                ? <ChevronDown className="h-4 w-4 text-blue-600 flex-shrink-0" />
                : <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />}
            </div>
          </button>

          {/* Expanded body — read-only batch list */}
          {pubPanelOpen && (
            <div id="pub-panel-body" className="px-4 pb-4" data-testid="pub-panel-body">
              <p className="text-sm text-blue-800 mb-3">
                {approvedUnpublishedBatches.length === 1
                  ? 'This batch has been approved but has not been published yet — auditors and clients cannot see it.'
                  : 'These batches have been approved but not yet published — auditors and clients cannot see them.'}
                {' '}Use <span className="font-semibold">Approve &amp; Publish</span> above to select and release jobs.
              </p>
              <div className="max-h-56 overflow-y-auto space-y-2 pr-0.5">
                {approvedUnpublishedBatches.map(batch => {
                  const isSingleOrBB = batch.is_bb_batch || batch.job_count <= 1;
                  const dateRange = batch.first_job_date
                    ? (batch.last_job_date && batch.last_job_date !== batch.first_job_date
                        ? `${batch.first_job_date} → ${batch.last_job_date}`
                        : batch.first_job_date)
                    : null;
                  return (
                  <div key={batch.id} className="flex items-center gap-2 bg-white border border-blue-200 rounded-md px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-blue-800 block truncate">
                        {batch.name || `Batch #${batch.id}`}
                        {batch.is_bb_batch && <span className="ml-1 text-blue-500">· BB</span>}
                      </span>
                      <span className="text-xs text-blue-600 block">
                        {isSingleOrBB ? (
                          <>
                            {batch.primary_site_name || '—'}
                            {batch.primary_auditor_name ? ` · ${batch.primary_auditor_name}` : ''}
                          </>
                        ) : (
                          <>
                            {batch.job_count || 0} job{batch.job_count !== 1 ? 's' : ''}
                            {batch.proposal_count > 0 ? `, ${batch.proposal_count} change(s)` : ''}
                          </>
                        )}
                        {dateRange && <span className="ml-1 text-blue-500">· {dateRange}</span>}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Filters - hide advanced filters for auditors */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Row 1: Date Range for all, Search for non-auditors */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search input - visible to all internal users */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by site or auditor..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="schedule-search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">From</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setStartDate(newStart);
                    // Auto-adjust end date if it would be before start date
                    if (endDate && newStart > endDate) {
                      setEndDate(newStart);
                    }
                  }}
                  className="w-[150px]"
                  data-testid="schedule-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">To</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    // Prevent end date before start date
                    if (startDate && newEnd < startDate) {
                      toast({ title: 'Invalid Date', description: 'End date cannot be before start date', variant: 'destructive' });
                      return;
                    }
                    setEndDate(newEnd);
                  }}
                  min={startDate}
                  className={cn("w-[150px]", endDate && startDate && endDate < startDate && "border-red-500")}
                  data-testid="schedule-end-date"
                />
              </div>
              
              {/* Quick Date Range Buttons */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1" data-testid="quick-date-range-btn">
                    <CalendarRange className="h-4 w-4" />
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* iter450 — Day-level shortcuts (Today / Yesterday / Tomorrow)
                      grouped above the month/quarter presets so officers can
                      quickly jump to a single-day view. */}
                  <DropdownMenuItem onClick={() => {
                    const now = new Date();
                    const d = format(now, 'yyyy-MM-dd');
                    setStartDate(d); setEndDate(d);
                  }} data-testid="date-shortcut-today">
                    Today
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const y = new Date(); y.setDate(y.getDate() - 1);
                    const d = format(y, 'yyyy-MM-dd');
                    setStartDate(d); setEndDate(d);
                  }} data-testid="date-shortcut-yesterday">
                    Yesterday
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const t = new Date(); t.setDate(t.getDate() + 1);
                    const d = format(t, 'yyyy-MM-dd');
                    setStartDate(d); setEndDate(d);
                  }} data-testid="date-shortcut-tomorrow">
                    Tomorrow
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    const now = new Date();
                    setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
                  }} data-testid="date-shortcut-this-month">
                    This Month
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const lastMonth = subMonths(new Date(), 1);
                    setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
                  }} data-testid="date-shortcut-last-month">
                    Last Month
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    const nextMonth = addMonths(new Date(), 1);
                    setStartDate(format(startOfMonth(nextMonth), 'yyyy-MM-dd'));
                    setEndDate(format(endOfMonth(nextMonth), 'yyyy-MM-dd'));
                  }} data-testid="date-shortcut-next-month">
                    Next Month
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    const now = new Date();
                    setStartDate(format(startOfQuarter(now), 'yyyy-MM-dd'));
                    setEndDate(format(endOfQuarter(now), 'yyyy-MM-dd'));
                  }} data-testid="date-shortcut-this-quarter">
                    This Quarter
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Row 2: Additional filters for all internal users */}
            <div className="flex flex-col sm:flex-row gap-4">
              <RegionFilter value={regionId} onChange={setRegionId} />
              {/* Combined Customer/Site Search */}
              <SiteCustomerCombobox
                  clients={clients}
                  sites={sites}
                  value={siteCustomerFilter}
                  onValueChange={setSiteCustomerFilter}
                  placeholder="All Customers & Sites"
                  className="w-full sm:w-[280px]"
                  data-testid="schedule-site-customer-filter"
                />
                
                <Select value={auditorFilter || "all"} onValueChange={(v) => setAuditorFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="schedule-auditor-filter">
                    <SelectValue placeholder="All Auditors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Auditors</SelectItem>
                    {auditors.map((auditor) => (
                      <SelectItem key={auditor.id} value={auditor.id.toString()}>
                        {auditor.first_name} {auditor.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="schedule-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                    <Filter className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Table */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>{(isAuditor && !isAdmin && !isSeniorScheduler && !isSchedulerCtx) ? 'My Assignments' : 'Schedule'}</CardTitle>
              <CardDescription>
                {displayedJobs.length} entr{displayedJobs.length !== 1 ? 'ies' : 'y'}
                {startDate && endDate && ` from ${format(new Date(startDate), 'd MMM')} to ${format(new Date(endDate), 'd MMM yyyy')}`}
              </CardDescription>
            </div>
            {/* Job-type filter toggle */}
            <div className="flex rounded-md border border-border overflow-hidden shrink-0" data-testid="job-type-filter">
              {[
                { value: 'JOB', label: 'Jobs' },
                { value: 'ALL', label: 'All' },
                { value: 'NON_JOB', label: 'Leave' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setJobTypeFilter(value)}
                  data-testid={`job-type-filter-${value.toLowerCase()}`}
                  className={`px-3 py-1 text-xs font-medium transition-colors focus:outline-none
                    ${jobTypeFilter === value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : displayedJobs.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {(isAuditor && !isAdmin && !isSeniorScheduler && !isSchedulerCtx) ? 'No assignments in this date range' : 'No schedule entries'}
              </p>
              {canManageJobs && (
                <Button onClick={openCreateDialog} variant="outline" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Add first entry
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Auditors</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Status</TableHead>
                  {(canManageJobs || isAuditor) && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedJobs.map((job) => (
                  <TableRow
                    key={job.id}
                    data-testid={`schedule-row-${job.id}`}
                    className={job.is_locked ? 'border-l-[3px] border-l-amber-500 bg-amber-50/30' : ''}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        {job.is_locked && (
                          <div className="mb-1">
                            <LockBadge job={job} />
                          </div>
                        )}
                        {/* Check for non-consecutive dates first */}
                        {job.job_dates && job.job_dates.length > 1 && job.job_dates.length !== 
                          (job.end_date && job.scheduled_date ? 
                            Math.ceil((new Date(job.end_date) - new Date(job.scheduled_date)) / (1000 * 60 * 60 * 24)) + 1 : 1) ? (
                          <>
                            <span className="font-medium">
                              {job.job_dates.slice(0, 3).map((d, i) => (
                                <span key={d}>
                                  {i > 0 && ', '}
                                  {format(new Date(d), 'd MMM')}
                                </span>
                              ))}
                              {job.job_dates.length > 3 && <span className="text-muted-foreground"> +{job.job_dates.length - 3} more</span>}
                            </span>
                            <Badge variant="outline" className="w-fit mt-1 text-xs">
                              {job.job_dates.length} non-consecutive days
                            </Badge>
                          </>
                        ) : (
                          <>
                            <span className="font-medium">
                              {format(new Date(job.scheduled_date), 'd MMM yyyy')}
                              {job.end_date && job.end_date !== job.scheduled_date && (
                                <span className="text-muted-foreground"> → {format(new Date(job.end_date), 'd MMM yyyy')}</span>
                              )}
                            </span>
                            {job.end_date && job.end_date !== job.scheduled_date && (
                              <Badge variant="outline" className="w-fit mt-1 text-xs">
                                Multi-day
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {/* Show leave type badge for leave events, client color dot for jobs */}
                        {job.event_type?.startsWith('LEAVE') ? (
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              job.event_type === 'LEAVE_HOLIDAY' 
                                ? 'bg-green-100 text-green-800 border-green-300' 
                                : job.event_type === 'LEAVE_OTHER'
                                ? 'bg-slate-200 text-slate-700'
                                : ''
                            }`}
                          >
                            {job.event_type === 'LEAVE_HOLIDAY' ? '🏖️ Holiday' : 
                             job.event_type === 'LEAVE_SICK' ? '🤒 Sick' : 
                             job.event_type === 'LEAVE_COMPASSIONATE' ? '💜 Compassionate' :
                             job.event_type === 'LEAVE_OTHER' ? `📋 ${job.notes || 'Other'}` : 'Leave'}
                          </Badge>
                        ) : (
                          <>
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: job.site?.parent_client?.colour || job.site?.independent_client?.colour || '#6B7280' }}
                              title={job.site?.parent_client?.name || job.site?.independent_client?.name || 'Unassigned'}
                            />
                            <span>{job.site?.name || '-'}</span>
                            {/* Phase 2A: Show proposal badge if this job has a pending edit/delete proposal */}
                            {myProposals.some(p => p.original_job_id === job.id) && (
                              <Badge variant="outline" className="ml-1 bg-purple-50 text-purple-700 border-purple-300 text-[10px]">
                                <FileEdit className="h-3 w-3 mr-0.5" />
                                {myProposals.find(p => p.original_job_id === job.id)?.proposal_type === 'DELETE' ? 'Delete' : 'Edit'}
                              </Badge>
                            )}
                            {/* Approved Draft badge — only for internal scheduling roles.
                                Indicates the batch is APPROVED but not yet published (published_at = NULL).
                                Auditor-only and client users never see these jobs, so we only need
                                to show this badge to roles that CAN see approved-unpublished rows. */}
                            {job.is_unpublished_approved && (
                              <Badge
                                variant="outline"
                                className="ml-1 bg-blue-50 text-blue-700 border-blue-300 text-[10px]"
                                title="Approved Draft — not yet published to auditors/clients"
                                data-testid={`approved-draft-badge-${job.id}`}
                              >
                                <Clock className="h-3 w-3 mr-0.5" />
                                Approved Draft
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{
                          // Derive service name from pricing lines (single source of truth)
                          (job.pricing_lines?.length > 0
                            ? [...new Set(job.pricing_lines.map(pl => pl.service_name).filter(Boolean))].join(' & ') || job.service?.name
                            : job.service?.name) || '-'
                        }</p>
                        {job.client_reference && (
                          <p className="text-xs text-muted-foreground">Ref: {job.client_reference}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {job.auditors?.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {/* Check if this job has per-auditor assignments with different dates */}
                          {job.assignments && job.assignments.length > 0 && job.auditors.length > 1 ? (
                            // Show per-auditor dates when they differ
                            <>
                              {job.auditors.map(a => {
                                // iter454 — an auditor may have multiple
                                // non-contiguous assignment rows (split dates
                                // like 14 Jul + 15 Jul as separate rows).
                                // Show every range, comma-separated, so the
                                // Schedule list matches what the Matrix shows.
                                const auditorAssignments = (job.assignments || [])
                                  .filter(asn => asn.auditor_id === a.id)
                                  .sort((x, y) => (x.start_date || '').localeCompare(y.start_date || ''));
                                const dateRange = auditorAssignments.length > 0
                                  ? auditorAssignments.map(assignment => (
                                      assignment.start_date === assignment.end_date
                                        ? format(new Date(assignment.start_date), 'd MMM')
                                        : `${format(new Date(assignment.start_date), 'd MMM')}→${format(new Date(assignment.end_date), 'd')}`
                                    )).join(', ')
                                  : null;
                                return (
                                  <div key={a.id} className="flex items-center justify-between gap-2 text-xs">
                                    <span className="flex items-center gap-1">
                                      <User className="h-3 w-3 text-muted-foreground" />
                                      {a.first_name} {a.last_name.charAt(0)}.
                                    </span>
                                    {dateRange && (
                                      <span className="text-muted-foreground bg-slate-100 px-1 rounded text-[10px]">{dateRange}</span>
                                    )}
                                  </div>
                                );
                              })}
                              <Badge variant="outline" className="w-fit mt-0.5 text-[10px]">
                                Split Dates
                              </Badge>
                            </>
                          ) : (
                            // Standard display - all auditors, same dates
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">
                                {job.auditors.map(a => `${a.first_name} ${a.last_name.charAt(0)}.`).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-amber-600 text-sm font-medium">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-muted-foreground">{job.notes || '-'}</span>
                        {job._pendingSync && (
                          <CloudOff className="h-3 w-3 text-amber-500" title="Pending sync" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        job.event_type?.startsWith('LEAVE') 
                          ? 'secondary'  // Leave events always use grey (differentiated)
                          : statusColors[job.status]  // Regular jobs use status-based colors
                      }>
                        {/* iter450 — Non-job entries (holidays / leave / meetings)
                            show "Booked" instead of "Scheduled" so officers don't
                            confuse them with actionable job entries. The grey
                            badge variant is retained for extra visual separation. */}
                        {job.event_type?.startsWith('LEAVE') && job.status === 'scheduled'
                          ? 'Booked'
                          : statusLabels[job.status]}
                      </Badge>
                    </TableCell>
                    {canManageJobs && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {/* Single button: Reports page for completed client jobs, Job Detail for others */}
                          {job.status === 'completed' && job.event_type !== 'LEAVE_HOLIDAY' && job.event_type !== 'LEAVE_SICK' && job.event_type !== 'LEAVE_COMPASSIONATE' && job.event_type !== 'LEAVE_OTHER' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="View Audit Reports"
                              data-testid={`reports-${job.id}-btn`}
                              className="h-8 w-8"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              <ClipboardList className="h-4 w-4 text-emerald-600" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="View Job Detail"
                              data-testid={`view-job-${job.id}-btn`}
                              className="h-8 w-8"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              <ExternalLink className="h-4 w-4 text-slate-600" />
                            </Button>
                          )}
                          {job.status === 'scheduled' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(job)}
                                title="Edit"
                                data-testid={`edit-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStatusChange(job, 'completed')}
                                title={job.auditors?.length > 0 ? "Mark Complete" : "Assign auditors first"}
                                disabled={!job.auditors || job.auditors.length === 0}
                                data-testid={`complete-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <CheckCircle className={`h-4 w-4 ${job.auditors?.length > 0 ? 'text-green-600' : 'text-gray-300'}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleStatusChange(job, 'cancelled')}
                                title="Cancel"
                                data-testid={`cancel-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <XCircle className="h-4 w-4 text-red-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => duplicateJob(job)}
                                title="Duplicate"
                                data-testid={`duplicate-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Copy className="h-4 w-4 text-blue-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openCopyDialog(job)}
                                title="Copy to Date"
                                data-testid={`copy-to-date-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <CopyPlus className="h-4 w-4 text-purple-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleMarkJobNotified(job.id)}
                                title={job.notification_sent ? "Notification sent" : "Mark as Notified"}
                                data-testid={`mark-notified-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                {job.notification_sent ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Mail className="h-4 w-4 text-blue-500" />
                                )}
                              </Button>
                              {/* iter340 v8e — Lock toggle (Senior Scheduler / Admin only). */}
                              <LockToggleButton
                                job={job}
                                onChange={(updated) => setJobs((prev) => prev.map((j) => j.id === updated.id ? { ...j, ...updated } : j))}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(job)}
                                data-testid={`delete-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          ) : job.status === 'completed' || job.status === 'cancelled' ? (
                            <>
                              {/* Edit and Delete for completed/cancelled jobs (with warning) */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(job)}
                                title="Edit (completed job)"
                                data-testid={`edit-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Pencil className="h-4 w-4 text-amber-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(job)}
                                title="Delete (completed job)"
                                data-testid={`delete-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    )}
                    {/* Auditor Actions - Edit/Delete via proposals (no pricing visible) */}
                    {isAuditor && !canManageJobs && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {/* Notes button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openNotesDialog(job)}
                            title="Add/Edit Notes"
                            data-testid={`auditor-notes-${job.id}-btn`}
                            className="h-8 w-8"
                          >
                            <StickyNote className="h-4 w-4" />
                          </Button>
                          {/* View Job Detail — available for ALL job statuses including pre-completion (audit wizard & file uploads) */}
                          {!job.event_type?.startsWith('LEAVE') && (
                            job.status === 'completed' ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View Audit Reports"
                                data-testid={`auditor-reports-${job.id}-btn`}
                                className="h-8 w-8"
                                onClick={() => navigate(`/jobs/${job.id}`)}
                              >
                                <ClipboardList className="h-4 w-4 text-emerald-600" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View Job Detail / Audit Wizard"
                                data-testid={`auditor-view-job-${job.id}-btn`}
                                className="h-8 w-8"
                                onClick={() => navigate(`/jobs/${job.id}`)}
                              >
                                <ExternalLink className="h-4 w-4 text-slate-600" />
                              </Button>
                            )
                          )}
                          {job.status === 'scheduled' && (
                            <>
                              {/* Edit (creates proposal) */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(job)}
                                title="Propose Edit"
                                data-testid={`auditor-edit-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <FileEdit className="h-4 w-4 text-purple-500" />
                              </Button>
                              {/* Delete (creates proposal) */}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDeleteDialog(job)}
                                title="Propose Deletion"
                                data-testid={`auditor-delete-${job.id}-btn`}
                                className="h-8 w-8"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {/* iter340 v8e — lock legend explaining the amber-bordered rows */}
        <div className="px-6 pb-4 pt-2">
          <LockLegend />
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) { setBlockPairs([]); setBlockPairStatuses({}); }
      }}>
        {/* iter449 — `overflow-hidden` on DialogContent guarantees the dialog
            never grows beyond `max-h-[85vh]`. `min-h-0` on the inner scroll
            container defeats the flexbox default `min-height: auto` which
            otherwise stops `overflow-y-auto` from engaging when per-scope
            completion + multi-line pricing pushes the form past 85vh. */}
        <DialogContent className={`max-h-[85vh] flex flex-col overflow-hidden ${formData.event_type?.startsWith('LEAVE') ? 'max-w-md' : 'max-w-lg'}`}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {formData.event_type?.startsWith('LEAVE') 
                ? (selectedJob ? 'Edit Leave/Holiday' : 'Add Leave/Holiday')
                : (selectedJob ? 'Edit Entry' : 'Add Schedule Entry')
              }
            </DialogTitle>
            <DialogDescription>
              {formData.event_type?.startsWith('LEAVE')
                ? 'Update leave details'
                : (selectedJob ? 'Update schedule details' : 'Create schedule entries for one or more dates')
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="space-y-4 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 pr-2" data-testid="edit-entry-scroll-body">
              
              {/* LEAVE EVENT - Simplified Dialog */}
              {formData.event_type?.startsWith('LEAVE') && selectedJob && (
                <>
                  {/* Leave Type */}
                  <div className="space-y-2">
                    <Label>Leave Type *</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => handleChange('event_type', value)}
                    >
                      <SelectTrigger data-testid="leave-type-select">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LEAVE_HOLIDAY">🏖️ Holiday</SelectItem>
                        <SelectItem value="LEAVE_SICK">🤒 Sick Leave</SelectItem>
                        <SelectItem value="LEAVE_COMPASSIONATE">💜 Compassionate Leave</SelectItem>
                        <SelectItem value="LEAVE_OTHER">📋 Other (Meeting, Training, etc.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Custom description for "Other" leave type */}
                  {formData.event_type === 'LEAVE_OTHER' && (
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Input
                        value={formData.notes || ''}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="e.g., Team meeting, Training, Conference"
                        data-testid="other-leave-description"
                      />
                    </div>
                  )}
                  
                  {/* Person (read-only display) */}
                  <div className="space-y-2">
                    <Label>Person</Label>
                    <div className="p-2 bg-slate-50 rounded-md text-sm">
                      {selectedJob?.auditors?.map(a => `${a.first_name} ${a.last_name}`).join(', ') || 'Not assigned'}
                    </div>
                  </div>
                  
                  {/* Duration - Start Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="scheduled_date">Start Date *</Label>
                      <Input
                        id="scheduled_date"
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => handleChange('scheduled_date', e.target.value)}
                        required
                        data-testid="leave-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleChange('end_date', e.target.value)}
                        min={formData.scheduled_date || undefined}
                        data-testid="leave-end-date"
                      />
                    </div>
                  </div>
                  
                  {/* Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Optional notes..."
                      rows={2}
                      data-testid="leave-notes"
                    />
                  </div>
                </>
              )}
              
              {/* CLIENT JOB - Full Dialog (or creating new entries) */}
              {(!formData.event_type?.startsWith('LEAVE') || !selectedJob) && (
              <>
              {/* Customer/Site FIRST - this is the primary selection */}
              <div className="space-y-2">
                <Label htmlFor="site_id">Customer / Site *</Label>
                <SiteCustomerCombobox
                  clients={clients}
                  sites={sites}
                  value={formData.site_id}
                  onValueChange={(value) => handleChange('site_id', value)}
                  placeholder="Search customer or site..."
                  siteOnly={true}
                  className="w-full"
                  data-testid="entry-site-select"
                />
                {/* Fallback message for inactive/unavailable sites */}
                {formData.site_id && selectedJob?.site && !selectedSite && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Current site ({selectedJob.site.name}) is inactive or unavailable. Select a new site.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Pricing lines will load automatically from site defaults
                </p>
              </div>

              {/* iter492 — Service Type (primary service_id on the job).
                  Mirrors the batch entry side panel — every job has a
                  single primary Service Type in addition to its
                  pricing-line breakdown. */}
              <div className="space-y-2">
                <Label htmlFor="job-service-type">Service Type</Label>
                <select
                  id="job-service-type"
                  value={formData.service_id || ''}
                  onChange={(e) => handleChange('service_id', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="entry-service-type-select"
                >
                  <option value="">— Select service type —</option>
                  {services.map(svc => (
                    <option key={svc.id} value={String(svc.id)}>{svc.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Primary service for this job. Aligns with the batch's Service Type and drives per-scope treatment when the pricing lines resolve to a per-scope-enabled parent.
                </p>
              </div>

              {/* Date Selection — single or block mode */}
              <div className="space-y-3">
                {/* Block mode: show summary link; single mode: show date inputs */}
                {!selectedJob && blockPairs.length > 0 ? (
                  <div className="space-y-1">
                    <Label>Date(s) *</Label>
                    <button
                      type="button"
                      onClick={() => setBlockDialogOpen(true)}
                      className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2"
                      data-testid="block-dates-summary-link"
                    >
                      <CalendarRange className="h-3.5 w-3.5" />
                      {blockPairs.length} date range{blockPairs.length !== 1 ? 's' : ''} — edit
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input
                          type="date"
                          value={formData.scheduled_date}
                          onChange={(e) => handleChange('scheduled_date', e.target.value)}
                          required={blockPairs.length === 0}
                          data-testid="entry-start-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date <span className="text-muted-foreground text-xs">(optional, for multi-day)</span></Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => handleChange('end_date', e.target.value)}
                          min={formData.scheduled_date}
                          className={formData.end_date && formData.scheduled_date && formData.end_date < formData.scheduled_date ? 'border-red-500' : ''}
                          data-testid="entry-end-date-input"
                        />
                        {formData.end_date && formData.scheduled_date && formData.end_date < formData.scheduled_date && (
                          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            End date cannot be before start date
                          </p>
                        )}
                      </div>
                    </div>
                    {formData.end_date && formData.scheduled_date && formData.end_date > formData.scheduled_date && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CalendarRange className="h-3 w-3" />
                        This will be a multi-day job spanning {Math.ceil((new Date(formData.end_date) - new Date(formData.scheduled_date)) / (1000 * 60 * 60 * 24)) + 1} days
                      </p>
                    )}
                  </>
                )}

                {/* Block booking trigger — new entry only */}
                {!selectedJob && (
                  <button
                    type="button"
                    onClick={() => setBlockDialogOpen(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    data-testid="bb-open-btn"
                  >
                    <CalendarRange className="h-3 w-3" />
                    {blockPairs.length > 0
                      ? `${blockPairs.length} date range${blockPairs.length !== 1 ? 's' : ''} saved`
                      : 'Book multiple date ranges'}
                  </button>
                )}

                {/* Per-pair submit status (visible during / after block submit) */}
                {!selectedJob && blockPairs.length > 0 && Object.keys(blockPairStatuses).length > 0 && (
                  <div className="rounded-md border p-2 space-y-1 bg-slate-50" data-testid="bb-status-list">
                    {blockPairs.map(pair => {
                      const s = blockPairStatuses[pair.id];
                      return (
                        <div key={pair.id} className="flex items-center gap-2 text-xs">
                          {(!s || s.status === 'pending') && <span className="text-slate-400">•</span>}
                          {s?.status === 'submitting'  && <Loader2   className="h-3 w-3 animate-spin text-blue-500" />}
                          {s?.status === 'success'     && <CheckCircle className="h-3 w-3 text-green-600" />}
                          {s?.status === 'error'       && <XCircle    className="h-3 w-3 text-red-500" />}
                          {s?.status === 'dup_confirm' && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                          <span className="font-mono">
                            {pair.start}{pair.end && pair.end !== pair.start ? ` → ${pair.end}` : ''}
                          </span>
                          {s?.status === 'error' && <span className="text-red-500 truncate max-w-[16rem]">{s.errorMsg}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Auditors <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {auditors.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No auditors available</p>
                  ) : (
                    auditors.map((auditor) => (
                      <div key={auditor.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`auditor-${auditor.id}`}
                          checked={formData.auditor_ids.includes(auditor.id)}
                          onCheckedChange={() => toggleAuditor(auditor.id)}
                        />
                        <label
                          htmlFor={`auditor-${auditor.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {auditor.first_name} {auditor.last_name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.auditor_ids.length === 0 && (
                  <p className="text-xs text-amber-600">Jobs must be assigned before completion</p>
                )}
              </div>

              {/* iter419 — Per-scope completion (Food / Beverage Stock Audit).
                  When the job has scope_completions rows, the admin/scheduler
                  can mark each scope (Food + Beverage) complete independently
                  with its own date AND assign a different set of auditors per
                  scope (iter419d). The parent status auto-rolls up to
                  COMPLETED when both are done.
                  iter473 — Senior Schedulers see & edit this section too
                  (SS = admin-equivalent for completion mgmt). Regular
                  schedulers/auditors still go via proposal workflow. */}
              {selectedJob && (!isProposalMode || isSeniorScheduler) && isPerScopeJob(selectedJob) && (
                <div className="space-y-2 border rounded-md p-3 bg-muted/30" data-testid="scope-completion-section">
                  <Label className="text-sm font-semibold">Per-Scope Completion</Label>
                  <p className="text-xs text-muted-foreground -mt-1">
                    Food and Beverage halves can be completed on different days by different auditors. Changes save when you press <strong>Update</strong> at the bottom; unsaved rows show a yellow dot. The overall <strong>Status</strong> (below) auto-flips to Completed once <strong>every</strong> scope is marked done — it is the single source of truth used by reports and KPIs.
                  </p>
                  {selectedJob.scope_completions.map((sc) => {
                    // Minimum is the job START date — a scope element can be completed
                    // on any day within the scheduled window, not only on the last day.
                    // sc.scheduled_date is NOT used here because it can be set to
                    // end_date for multi-day jobs, which incorrectly blocks start-date
                    // completions.
                    const schedMin = selectedJob.scheduled_date;
                    const schedMax = selectedJob.end_date || selectedJob.scheduled_date;
                    const draft = scopeDrafts[sc.scope] || { completed_on: '', status: 'scheduled', auditor_ids: [], dirty: false };
                    return (
                      <div key={sc.scope} className="flex flex-col gap-1.5 border-b pb-2 last:border-b-0 last:pb-0 sm:grid sm:grid-cols-[100px_140px_1fr_auto] sm:items-start sm:gap-2 sm:border-0 sm:pb-0 text-sm">
                        <span className="font-medium sm:pt-1.5 flex items-center gap-1">
                          {sc.scope === 'FOOD' ? 'Food' : 'Beverage'}
                          {draft.dirty && (
                            <span
                              className="inline-block h-2 w-2 rounded-full bg-amber-500"
                              title="Unsaved changes — press Update to save"
                              data-testid={`scope-dirty-${sc.scope.toLowerCase()}`}
                            />
                          )}
                        </span>
                        <Input
                          type="date"
                          value={draft.completed_on || ''}
                          min={schedMin || undefined}
                          onChange={(e) => {
                            const newDate = e.target.value || '';
                            if (newDate && schedMin && newDate < schedMin) {
                              toast({
                                title: 'Date too early',
                                description: `Completion date cannot be before the job start date (${schedMin}).`,
                                variant: 'destructive',
                              });
                              return;
                            }
                            setScopeDrafts(prev => ({
                              ...prev,
                              [sc.scope]: {
                                ...(prev[sc.scope] || draft),
                                completed_on: newDate,
                                status: newDate ? 'completed' : 'scheduled',
                                dirty: true,
                              },
                            }));
                          }}
                          className="h-8 text-xs w-full"
                          data-testid={`scope-completion-date-${sc.scope.toLowerCase()}`}
                        />
                        <ScopeAuditorPicker
                          jobId={selectedJob.id}
                          scope={sc.scope}
                          auditors={auditors}
                          selectedIds={draft.auditor_ids}
                          onChange={(newIds) => {
                            setScopeDrafts(prev => ({
                              ...prev,
                              [sc.scope]: {
                                ...(prev[sc.scope] || draft),
                                auditor_ids: newIds,
                                dirty: true,
                              },
                            }));
                          }}
                        />
                        <span className="text-xs sm:pt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className={draft.status === 'completed' ? 'text-emerald-600' : 'text-muted-foreground'} data-testid={`scope-status-${sc.scope.toLowerCase()}`}>
                            {draft.status === 'completed' ? `✓ ${draft.completed_on || ''}` : 'Pending'}
                          </span>
                          {draft.status === 'completed' && (
                            <button
                              type="button"
                              className="text-[10px] text-muted-foreground hover:text-rose-600 underline underline-offset-2"
                              title="Revert this scope back to Scheduled (clears the completion date)"
                              data-testid={`scope-revert-${sc.scope.toLowerCase()}`}
                              onClick={() => {
                                setScopeDrafts(prev => ({
                                  ...prev,
                                  [sc.scope]: {
                                    ...(prev[sc.scope] || draft),
                                    completed_on: '',
                                    status: 'scheduled',
                                    delay_justified: false,
                                    delay_justification_note: '',
                                    dirty: true,
                                  },
                                }));
                              }}
                            >
                              Revert
                            </button>
                          )}
                        </span>
                        {/* iter111 — Per-scope Delay Justified toggle.
                            Mirrors the job-level routine but scoped to this
                            scope so Food and Beverage auditors are exempted
                            independently. Only shown when the scope is
                            completed (same visibility rule as the job-level
                            block). */}
                        {draft.status === 'completed' && (
                          <div className="sm:col-span-4 mt-0.5 space-y-1" data-testid={`scope-delay-section-${sc.scope.toLowerCase()}`}>
                            <label className="flex items-center gap-2 cursor-pointer" data-testid={`scope-delay-justified-label-${sc.scope.toLowerCase()}`}>
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5 rounded border-gray-300"
                                checked={!!draft.delay_justified}
                                data-testid={`scope-delay-justified-${sc.scope.toLowerCase()}`}
                                onChange={(e) => {
                                  setScopeDrafts(prev => ({
                                    ...prev,
                                    [sc.scope]: {
                                      ...(prev[sc.scope] || draft),
                                      delay_justified: e.target.checked,
                                      delay_justification_note: e.target.checked ? (draft.delay_justification_note || '') : '',
                                      dirty: true,
                                    },
                                  }));
                                }}
                              />
                              <span className="text-xs text-muted-foreground">
                                Delay Justified — {sc.scope === 'FOOD' ? 'Food' : 'Beverage'} auditor KPI exemption
                              </span>
                            </label>
                            {draft.delay_justified && (
                              <textarea
                                className="w-full text-xs rounded border border-input bg-background px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                                rows={2}
                                placeholder="Reason for delay (min 5 characters)…"
                                value={draft.delay_justification_note || ''}
                                data-testid={`scope-delay-note-${sc.scope.toLowerCase()}`}
                                onChange={(e) => {
                                  setScopeDrafts(prev => ({
                                    ...prev,
                                    [sc.scope]: {
                                      ...(prev[sc.scope] || draft),
                                      delay_justification_note: e.target.value,
                                      dirty: true,
                                    },
                                  }));
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* iter419g.2 — When per-scope completion is active, the
                  Status + Completion Date are AUTO-DERIVED from the
                  per-scope rows above. Lock the manual Status dropdown
                  so admins can't create drift (e.g. scopes complete but
                  Job.status still scheduled). For reports, the source
                  of truth is the parent `Job.status` / `Job.completed_on`
                  — auto-rolled-up by the backend when ALL scopes are
                  marked complete. */}
              {selectedJob && (!isProposalMode || isSeniorScheduler) && isPerScopeJob(selectedJob) && (
                <div className="space-y-1.5 border rounded-md p-3 bg-blue-50/50" data-testid="scope-completion-status-hint">
                  <Label className="text-sm font-semibold">Status</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant={formData.status === 'completed' ? 'default' : 'secondary'} className={formData.status === 'completed' ? 'bg-emerald-600' : ''}>
                      {formData.status === 'completed' ? 'Completed' : formData.status === 'cancelled' ? 'Cancelled' : 'Scheduled'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Auto-derived from Per-Scope Completion above. Mark every scope complete to flip this to <span className="font-semibold">Completed</span>.
                    </span>
                  </div>
                  {formData.status === 'completed' && formData.completed_on && (
                    <p className="text-[11px] text-muted-foreground">
                      Completion date (latest scope): {formData.completed_on}
                    </p>
                  )}
                </div>
              )}

              {/* Status - shown when editing AND not in proposal mode AND no per-scope rows.
                  iter473 — Senior Schedulers see this too, matching admin. */}
              {selectedJob && (!isProposalMode || isSeniorScheduler) && !isPerScopeJob(selectedJob) && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => handleChange('status', v)}
                  >
                    <SelectTrigger data-testid="entry-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="completed" disabled={formData.auditor_ids.length === 0}>
                        Completed {formData.auditor_ids.length === 0 && '(assign auditors first)'}
                      </SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Completion Date - only shown when status is completed AND no per-scope rows
                  (when per-scope rows exist, the completion date is the latest scope's
                  completed_on, surfaced read-only in the hint above). */}
              {selectedJob && formData.status === 'completed' && !isPerScopeJob(selectedJob) && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="completed_on">Completion Date *</Label>
                    <Input
                      id="completed_on"
                      type="date"
                      value={formData.completed_on}
                      onChange={(e) => handleChange('completed_on', e.target.value)}
                      min={format(new Date(new Date().setFullYear(new Date().getFullYear() - 2)), 'yyyy-MM-dd')}
                      max={format(new Date(new Date().setFullYear(new Date().getFullYear() + 2)), 'yyyy-MM-dd')}
                      required
                      data-testid="completion-date-edit-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      The actual date this job was completed. Used for KPI calculations.
                    </p>
                  </div>
                  
                  {/* Delay Justification Section in Edit Dialog */}
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="edit-delay-justified"
                        checked={formData.delay_justified}
                        onCheckedChange={(checked) => handleChange('delay_justified', checked)}
                        data-testid="edit-delay-justified-checkbox"
                      />
                      <Label htmlFor="edit-delay-justified" className="text-sm font-medium leading-none">
                        Delay justified
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      If checked, this job will be excluded from late/overdue KPI calculations.
                    </p>
                    
                    {formData.delay_justified && (
                      <div className="space-y-2">
                        <Label htmlFor="edit-delay-note">Justification Note *</Label>
                        <Textarea
                          id="edit-delay-note"
                          value={formData.delay_justification_note || ''}
                          onChange={(e) => handleChange('delay_justification_note', e.target.value)}
                          placeholder="Explain why the delay is justified..."
                          className="min-h-[60px]"
                          data-testid="edit-delay-justification-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Minimum 5 characters required
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="client_reference">Reference</Label>
                <Input
                  id="client_reference"
                  value={formData.client_reference}
                  onChange={(e) => handleChange('client_reference', e.target.value)}
                  placeholder="e.g., PO-12345"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Optional notes"
                  className="min-h-[80px]"
                  data-testid="entry-notes-input"
                />
              </div>
              
              {/* Pricing Lines Section - Hidden in proposal mode */}
              {!isProposalMode && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Pricing Lines
                  </Label>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm" 
                    onClick={() => openFormPricingDialog()}
                    data-testid="add-pricing-line-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
                
                {loadingPricing ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : formPricingLines.length > 0 ? (
                  <div className="border rounded overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs py-1">Service</TableHead>
                          <TableHead className="text-xs py-1 text-right">Price</TableHead>
                          <TableHead className="text-xs py-1 w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formPricingLines.map((line, index) => (
                          <TableRow key={line.id || `new-${index}`} className="text-sm">
                            <TableCell className="py-1">
                              <div className="font-medium text-xs">{line.service_name}</div>
                              {line.description && (
                                <div className="text-[10px] text-muted-foreground">{line.description}</div>
                              )}
                            </TableCell>
                            <TableCell className="py-1 text-right font-medium">
                              {formatCurrency ? formatCurrency(line.price) : `${line.currency} ${line.price}`}
                            </TableCell>
                            <TableCell className="py-1">
                              <div className="flex gap-1">
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6"
                                  onClick={() => openFormPricingDialog(line, index)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 text-destructive"
                                  onClick={() => handleFormPricingLineDelete(index)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-3 text-center text-sm text-muted-foreground bg-slate-50 rounded border border-dashed">
                    {formData.site_id 
                      ? 'No pricing lines. Click "Add" to add one.' 
                      : 'Select a site to load default pricing.'}
                  </div>
                )}
                
                {/* Forecast Total */}
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">Forecast Total:</span>
                    <span className="font-bold text-green-700 text-lg">
                      {formatCurrency ? formatCurrency(formForecastTotal) : `£${formForecastTotal.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Sum of {formPricingLines.length} pricing line{formPricingLines.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              )}
              {/* End pricing section conditional */}
              </>
              )}
              {/* End of CLIENT JOB conditional block */}
            </div>
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              {/* Delete button for leave events */}
              {formData.event_type?.startsWith('LEAVE') && selectedJob && (
                <Button 
                  type="button" 
                  variant="destructive"
                  onClick={() => {
                    setDialogOpen(false);
                    setDeleteDialogOpen(true);
                  }}
                  data-testid="delete-leave-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button 
                type="submit" 
                disabled={saving || (formData.event_type?.startsWith('LEAVE') ? !formData.scheduled_date : (!formData.site_id || (blockPairs.length === 0 && !formData.scheduled_date))) || (blockPairs.length === 0 && formData.end_date && formData.scheduled_date && formData.end_date < formData.scheduled_date)}
                data-testid="save-entry-btn"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {saving ? 'Saving…' : (selectedJob ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Block Booking Dialog — date-pair editor (no jobs created here) */}
      <BlockBookingDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        initialPairs={blockPairs}
        onSave={(pairs) => {
          setBlockPairs(applyBlockPairKeys(pairs, blockPairs));
          setBlockPairStatuses({});
          // Assign ONE group key per BB session; preserve on edit so
          // reopening the dialog does not fork into a second BB batch.
          if (pairs.length > 0 && !blockGroupKey) {
            setBlockGroupKey(crypto.randomUUID());
          } else if (pairs.length === 0) {
            setBlockGroupKey(null);
          }
        }}
      />

      {/* Pricing Line Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingPricingLine ? 'Edit Pricing Line' : 'Add Pricing Line'}
            </DialogTitle>
            <DialogDescription>
              {editingPricingLine ? 'Modify the service and price for this line.' : 'Add a service with its price to this job.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service *</Label>
              <Select 
                value={pricingForm.service_id} 
                onValueChange={async (value) => {
                  // iter358 — auto-fill price from the site's master pricing
                  // when a service is picked on a NEW line and the user hasn't
                  // typed a price yet. Schedulers can pick a service even when
                  // they don't have direct pricing-edit access; if the site
                  // master has no entry for this service, leave the price at
                  // 0.00 with a hint that a Senior Scheduler can set it later.
                  setPricingForm(prev => ({ ...prev, service_id: value, _autofill_note: null }));
                  if (!formData.site_id) return;
                  if (editingPricingLine) return; // only auto-fill on Add
                  try {
                    const r = await api.get(`/site-pricing/default/${formData.site_id}/${value}`);
                    const masterPrice = r.data?.price;
                    if (masterPrice !== undefined && masterPrice !== null) {
                      setPricingForm(prev => ({
                        ...prev,
                        service_id: value,
                        price: String(masterPrice),
                        currency: r.data?.currency || prev.currency,
                        _autofill_note: `Auto-filled from site master (${r.data?.currency || ''} ${masterPrice})`,
                      }));
                    }
                  } catch (err) {
                    if (err?.response?.status === 404) {
                      setPricingForm(prev => ({
                        ...prev,
                        service_id: value,
                        price: prev.price || '0.00',
                        _autofill_note: 'Not priced for this site — set to 0.00. Senior Scheduler can update later.',
                      }));
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="pricing-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id.toString()}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pricingForm._autofill_note && (
                <p className="text-[11px] text-muted-foreground" data-testid="pricing-autofill-note">
                  {pricingForm._autofill_note}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Price ({currencySymbol}) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pricingForm.price}
                onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                data-testid="pricing-price-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select 
                value={pricingForm.currency} 
                onValueChange={(value) => setPricingForm(prev => ({ ...prev, currency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={pricingForm.description}
                onChange={(e) => setPricingForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Standard rate, Peak season"
                data-testid="pricing-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPricingDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleFormPricingLineSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editingPricingLine ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy to Date Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Copy to Date</DialogTitle>
            <DialogDescription>
              Copy this schedule entry to another date
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Target Date</Label>
            <Input
              type="date"
              value={copyToDate}
              onChange={(e) => setCopyToDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
            <Button onClick={copyJobToDate} disabled={!copyToDate}>
              <CopyPlus className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this schedule entry?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notes Dialog for Auditors */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              {selectedJobForNotes?.notes ? 'Edit Notes' : 'Add Notes'}
            </DialogTitle>
            <DialogDescription>
              {selectedJobForNotes?.site?.name} - {format(selectedJobForNotes?.scheduled_date ? new Date(selectedJobForNotes.scheduled_date) : new Date(), 'd MMM yyyy')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Notes</Label>
            <Textarea
              value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              placeholder="Add your notes here..."
              className="mt-2 min-h-[100px]"
              data-testid="auditor-notes-input"
            />
            {!isOnline && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <CloudOff className="h-3 w-3" />
                You're offline. Notes will sync when back online.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn">
              {savingNotes ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
              ) : !isOnline ? (
                <>Save Offline</>
              ) : (
                <>Save Notes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Date Dialog */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Mark as Completed
            </DialogTitle>
            <DialogDescription>
              {jobToComplete?.site?.name} - {jobToComplete?.service?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="completed-on-date">Completion Date *</Label>
              <Input
                id="completed-on-date"
                type="date"
                value={completedOnDate}
                onChange={(e) => setCompletedOnDate(e.target.value)}
                min={format(new Date(new Date().setFullYear(new Date().getFullYear() - 2)), 'yyyy-MM-dd')}
                max={format(new Date(new Date().setFullYear(new Date().getFullYear() + 2)), 'yyyy-MM-dd')}
                data-testid="completion-date-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter the actual date this job was completed. This is used for KPI calculations.
              </p>
            </div>
            
            {/* Delay Justification Section */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="delay-justified"
                  checked={delayJustified}
                  onCheckedChange={(checked) => setDelayJustified(checked)}
                  data-testid="delay-justified-checkbox"
                />
                <Label htmlFor="delay-justified" className="text-sm font-medium leading-none">
                  Delay justified
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                If checked, this job will be excluded from late/overdue KPI calculations.
              </p>
              
              {delayJustified && (
                <div className="space-y-2">
                  <Label htmlFor="delay-note">Justification Note *</Label>
                  <Input
                    id="delay-note"
                    value={delayJustificationNote}
                    onChange={(e) => setDelayJustificationNote(e.target.value)}
                    placeholder="Explain why the delay is justified..."
                    minLength={5}
                    required
                    data-testid="delay-justification-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum 5 characters required
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmComplete} 
              disabled={!completedOnDate || (delayJustified && (!delayJustificationNote || delayJustificationNote.trim().length < 5))}
              data-testid="confirm-complete-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Completion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog for Editing/Deleting Completed Jobs */}
      <AlertDialog open={completedJobWarningOpen} onOpenChange={setCompletedJobWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              {completedJobAction === 'edit' ? 'Edit Completed Job?' : 'Delete Completed Job?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This job has already been marked as <strong>completed</strong>.
                </p>
                {completedJobAction === 'edit' ? (
                  <p>
                    Editing a completed job may affect historical reports and KPI calculations. 
                    Only proceed if you need to correct an error.
                  </p>
                ) : (
                  <p>
                    Deleting a completed job will remove it from all reports and audit history. 
                    This action cannot be undone.
                  </p>
                )}
                {pendingJob && (
                  <div className="mt-3 p-3 bg-muted rounded-md text-sm">
                    <p><strong>Job:</strong> {pendingJob.site?.name} - {pendingJob.service?.name}</p>
                    <p><strong>Date:</strong> {pendingJob.scheduled_date}</p>
                    {pendingJob.completed_on && (
                      <p><strong>Completed:</strong> {pendingJob.completed_on}</p>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setCompletedJobWarningOpen(false);
              setPendingJob(null);
              setCompletedJobAction(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCompletedJobWarningConfirm}
              className={completedJobAction === 'delete' ? 'bg-destructive text-destructive-foreground' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {completedJobAction === 'edit' ? 'Edit Anyway' : 'Delete Anyway'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Schedule Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[95vh] h-[800px] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Publish Schedule
            </DialogTitle>
            <DialogDescription>
              Send schedule notifications to auditors and/or clients
            </DialogDescription>
          </DialogHeader>
          
          {publishLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : publishPreview ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Email Not Configured Warning */}
              {!emailConfigured && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Email Not Configured</p>
                      <p className="text-sm text-amber-700">
                        SendGrid API key is not configured. Notifications cannot be sent yet.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Date Range Info */}
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-md mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Date Range:</strong> {format(new Date(publishPreview.date_range_start), 'd MMM yyyy')} - {format(new Date(publishPreview.date_range_end), 'd MMM yyyy')}
                </p>
                {publishPreview.region && (
                  <p className="text-sm text-blue-800 mt-1">
                    <strong>Region:</strong> {publishPreview.region}
                  </p>
                )}
              </div>
              
              {/* Tabs for Auditors and Clients */}
              <Tabs value={publishTab} onValueChange={setPublishTab} className="flex-1 overflow-hidden flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="auditors" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Auditors ({selectedAuditorIds.length})
                  </TabsTrigger>
                  <TabsTrigger value="clients" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Clients ({selectedSiteIds.length})
                  </TabsTrigger>
                </TabsList>
                
                {/* Auditors Tab */}
                <TabsContent value="auditors" className="flex-1 overflow-auto space-y-4 mt-0">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-slate-900">{publishPreview.total_auditors}</p>
                      <p className="text-sm text-slate-600">Auditors</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-slate-900">{publishPreview.total_jobs}</p>
                      <p className="text-sm text-slate-600">Jobs</p>
                    </div>
                  </div>
                  
                  {/* Job Filters */}
                  <div className="space-y-3 p-3 bg-slate-50 border rounded-md">
                    <p className="text-sm font-medium text-slate-700">Filter Jobs</p>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={publishFilterUnnotifiedOnly}
                          onCheckedChange={(checked) => {
                            setPublishFilterUnnotifiedOnly(checked);
                            if (checked) {
                              setPublishFilterChangedOnly(false);
                              loadPublishPreview(true, false);
                            } else {
                              loadPublishPreview(false, publishFilterChangedOnly);
                            }
                          }}
                        />
                        <span className="text-sm text-slate-600">Only unnotified jobs</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={publishFilterChangedOnly}
                          onCheckedChange={(checked) => {
                            setPublishFilterChangedOnly(checked);
                            if (checked) {
                              setPublishFilterUnnotifiedOnly(false);
                              loadPublishPreview(false, true);
                            } else {
                              loadPublishPreview(publishFilterUnnotifiedOnly, false);
                            }
                          }}
                        />
                        <span className="text-sm text-slate-600">Only changed since last publish</span>
                      </label>
                    </div>
                    {(publishFilterUnnotifiedOnly || publishFilterChangedOnly) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPublishFilterUnnotifiedOnly(false);
                          setPublishFilterChangedOnly(false);
                          loadPublishPreview(false, false);
                        }}
                        className="text-xs h-6 px-2"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  
                  {/* Recipient Details with Checkboxes */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-700">
                        Recipients ({selectedAuditorIds.length} of {publishPreview.auditor_details.length} selected)
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllAuditors}
                        className="text-xs h-7"
                      >
                        {selectedAuditorIds.length === publishPreview.auditor_details.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Auditor</TableHead>
                            <TableHead className="text-right">Jobs</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {publishPreview.auditor_details.map((auditor) => (
                            <TableRow 
                              key={auditor.auditor_id}
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => toggleAuditorSelection(auditor.auditor_id)}
                            >
                              <TableCell className="w-10">
                                <Checkbox
                                  checked={selectedAuditorIds.includes(auditor.auditor_id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedAuditorIds(prev => [...prev, auditor.auditor_id]);
                                    } else {
                                      setSelectedAuditorIds(prev => prev.filter(id => id !== auditor.auditor_id));
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{auditor.name}</div>
                                <div className="text-xs text-muted-foreground">{auditor.email || '-'}</div>
                              </TableCell>
                              <TableCell className="text-right font-medium">{auditor.job_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Each selected auditor will receive an email with ALL their jobs in this date range attached as a calendar file.
                  </p>
                </TabsContent>
                
                {/* Clients Tab */}
                <TabsContent value="clients" className="flex-1 overflow-y-auto space-y-4 mt-0 pr-1">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-slate-900">{selectedSiteIds.length}</p>
                      <p className="text-sm text-slate-600">Sites Selected</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-slate-900">{filteredClientSites.length}</p>
                      <p className="text-sm text-slate-600">Sites with Jobs</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-emerald-600">
                        {filteredClientSites.filter(s => s.has_scheduling_contacts).length}
                      </p>
                      <p className="text-sm text-slate-600">With Contacts</p>
                    </div>
                  </div>
                  
                  {/* Loading indicator */}
                  {loadingClientPreview && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      <p className="text-sm text-blue-700">Loading sites with scheduled jobs...</p>
                    </div>
                  )}
                  
                  {/* Warning if all sites lack contacts */}
                  {!loadingClientPreview && filteredClientSites.length > 0 && 
                   filteredClientSites.filter(s => s.has_scheduling_contacts).length === 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-sm text-amber-800 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        <strong>No sites have contacts configured for Schedule Publication and Billing.</strong> 
                        Add contacts with the "Sched. Pub. &amp; Billing" flag enabled in Customers.
                      </p>
                    </div>
                  )}

                  {/* Site Selection - MOVED ABOVE FILTER for better UX */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-700">
                        Sites to Notify ({selectedSiteIds.length} of {filteredClientSites.length} selected)
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllSites}
                        className="text-xs h-7"
                        data-testid="toggle-all-sites"
                      >
                        {allFilteredSitesSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    
                    {/* Search and Filter within site selection */}
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search by customer or site name..."
                          value={clientSiteSearchQuery}
                          onChange={(e) => setClientSiteSearchQuery(e.target.value)}
                          className="pl-9 h-9"
                          data-testid="client-site-search"
                        />
                      </div>
                      {(clientSiteSearchQuery || clientFilterChangedSincePublish) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setClientSiteSearchQuery('');
                            setClientFilterChangedSincePublish(false);
                          }}
                          className="text-xs h-9 px-2"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    
                    {/* Filter checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox
                        checked={clientFilterChangedSincePublish}
                        onCheckedChange={(checked) => setClientFilterChangedSincePublish(checked)}
                        data-testid="client-filter-changed"
                      />
                      <span className="text-slate-600">Only show unpublished or changed since last publish</span>
                    </label>
                    
                    {/* Site Table */}
                    <div className="max-h-[350px] overflow-y-auto border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Site</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead className="text-center w-16">Jobs</TableHead>
                            <TableHead className="text-center w-24">Contacts</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredClientSites.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                {loadingClientPreview 
                                  ? 'Loading sites...' 
                                  : 'No sites have scheduled jobs in this date range'}
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredClientSites.map((site) => (
                              <TableRow 
                                key={site.id}
                                className={cn(
                                  "cursor-pointer hover:bg-slate-50",
                                  !site.has_scheduling_contacts && "bg-amber-50/50"
                                )}
                                onClick={() => toggleSiteSelection(site.id)}
                              >
                                <TableCell className="w-10">
                                  <Checkbox
                                    checked={selectedSiteIds.includes(site.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setSelectedSiteIds(prev => [...prev, site.id]);
                                      } else {
                                        setSelectedSiteIds(prev => prev.filter(id => id !== site.id));
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium text-sm">{site.name}</div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {site.customer_colour && (
                                      <div 
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: site.customer_colour }}
                                      />
                                    )}
                                    <span className="text-sm text-muted-foreground">{site.customer_name || '-'}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm font-medium">{site.job_count || 0}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                  {site.has_scheduling_contacts ? (
                                    <span className="text-xs text-emerald-600 flex items-center justify-center gap-1">
                                      <Check className="h-3 w-3" />
                                      {site.contacts?.length || 0}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-amber-600 flex items-center justify-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      None
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    <p className="text-xs text-muted-foreground">
                      <strong>What constitutes a change:</strong> New jobs added, job dates changed, services/line items modified, or auditor assignments updated.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Notifications sent to contacts with <strong>Schedule Publication and Billing</strong> flag enabled.
                    </p>
                  </div>
                  
                  {/* Email Script Info */}
                  <div className="p-3 bg-slate-50 border rounded-md">
                    <p className="text-sm font-medium text-slate-700 mb-2">Email Script Source</p>
                    <p className="text-xs text-slate-600">
                      Emails will use the script from <strong>Settings</strong> as the default. 
                      If a customer/site has a custom script configured, it will override the default.
                    </p>
                    {defaultEmailScript ? (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                        ✓ Default script configured in Settings ({defaultEmailScript.length} characters)
                      </div>
                    ) : (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                        ⚠ No default script configured in Settings - using system default template
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Send Mode - Always visible */}
              <div className="pt-4 border-t mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">When to Send</p>
                <div className="flex gap-3">
                  <Button
                    variant={sendMode === 'immediate' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMode('immediate')}
                    className="flex-1"
                    data-testid="send-mode-immediate"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Now
                  </Button>
                  <Button
                    variant={sendMode === 'batch_5pm' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMode('batch_5pm')}
                    className="flex-1"
                    data-testid="send-mode-batch"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Queue for 5 PM
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {sendMode === 'immediate' 
                    ? `Emails will be sent immediately to ${publishTab === 'auditors' ? selectedAuditorIds.length + ' auditor(s)' : selectedSiteIds.length + ' site(s)'}` 
                    : `Notifications will be batched and sent at 5 PM`}
                </p>
              </div>
            </div>
          ) : null}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setPublishDialogOpen(false);
              setPublishPreview(null);
              setSelectedAuditorIds([]);
              setSelectedSiteIds([]);
              setPublishTab('auditors');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={prepareConfirmation}
              disabled={!publishPreview || sendingNotifications || !emailConfigured || (publishTab === 'auditors' ? selectedAuditorIds.length === 0 : selectedSiteIds.length === 0)}
              data-testid="confirm-publish-btn"
            >
              {sendingNotifications ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> {sendMode === 'immediate' ? 'Send Now' : 'Queue for 5 PM'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Confirm Notification Recipients
            </AlertDialogTitle>
            <AlertDialogDescription>
              Review and confirm the recipients before sending. You can deselect any recipient to exclude them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            {/* Summary */}
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-md mb-4">
              <p className="text-sm text-blue-800">
                <strong>Date Range:</strong> {format(new Date(startDate), 'd MMM yyyy')} - {format(new Date(endDate), 'd MMM yyyy')}
              </p>
              <p className="text-sm text-blue-800 mt-1">
                <strong>Send Mode:</strong> {sendMode === 'immediate' ? 'Immediate' : 'Queued for 5 PM'}
              </p>
            </div>

            {/* Auditor Recipients */}
            {confirmationRecipients.auditors.length > 0 && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    Auditors ({confirmationRecipients.auditors.filter(a => a.selected).length} of {confirmationRecipients.auditors.length} selected)
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllConfirmationRecipients('auditors')}
                    className="text-xs h-7"
                  >
                    {confirmationRecipients.auditors.every(a => a.selected) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="max-h-[180px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Auditor</TableHead>
                        <TableHead className="text-right">Jobs</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {confirmationRecipients.auditors.map((auditor) => (
                        <TableRow 
                          key={auditor.auditor_id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleConfirmationRecipient('auditors', auditor.auditor_id)}
                        >
                          <TableCell className="w-10">
                            <Checkbox
                              checked={auditor.selected}
                              onCheckedChange={() => toggleConfirmationRecipient('auditors', auditor.auditor_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{auditor.name}</div>
                            <div className="text-xs text-muted-foreground">{auditor.email || '-'}</div>
                          </TableCell>
                          <TableCell className="text-right font-medium">{auditor.job_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Client Recipients (Sites) */}
            {confirmationRecipients.clients.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    Sites ({confirmationRecipients.clients.filter(c => c.selected).length} of {confirmationRecipients.clients.length} selected)
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAllConfirmationRecipients('clients')}
                    className="text-xs h-7"
                  >
                    {confirmationRecipients.clients.every(c => c.selected) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                
                {/* Warning for sites without contacts */}
                {confirmationRecipients.clients.some(s => !s.has_scheduling_contacts) && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-xs text-amber-800 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <strong>{confirmationRecipients.clients.filter(s => !s.has_scheduling_contacts).length} site(s)</strong> have no contacts with Schedule Publication and Billing enabled and will not receive emails.
                    </p>
                  </div>
                )}
                
                <div className="max-h-[250px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Site</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Contacts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {confirmationRecipients.clients.map((site) => (
                        <TableRow 
                          key={site.site_id}
                          className={cn(
                            "cursor-pointer hover:bg-slate-50",
                            !site.has_scheduling_contacts && "bg-amber-50/50"
                          )}
                          onClick={() => toggleConfirmationRecipient('clients', site.site_id)}
                        >
                          <TableCell className="w-10">
                            <Checkbox
                              checked={site.selected}
                              onCheckedChange={() => toggleConfirmationRecipient('clients', site.site_id)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={!site.has_scheduling_contacts}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{site.site_name}</div>
                            {site.job_count > 0 && (
                              <div className="text-xs text-muted-foreground">{site.job_count} job(s)</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {site.customer_colour && (
                                <div 
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: site.customer_colour }}
                                />
                              )}
                              <span className="text-sm text-muted-foreground">{site.customer_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {site.has_scheduling_contacts ? (
                              <div className="space-y-0.5">
                                {site.contacts.slice(0, 2).map((contact, idx) => (
                                  <div key={idx} className="text-xs">
                                    <span className="font-medium">{contact.name}</span>
                                    {contact.email && <span className="text-muted-foreground"> ({contact.email})</span>}
                                  </div>
                                ))}
                                {site.contacts.length > 2 && (
                                  <div className="text-xs text-muted-foreground">+{site.contacts.length - 2} more</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                No contacts
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground p-2 bg-blue-50 rounded border border-blue-100">
                  Emails will be sent to contacts with <strong>Schedule Publication and Billing</strong> flag enabled for each selected site.
                </p>
              </div>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDialogOpen(false)}>
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              disabled={sendingNotifications || (confirmationRecipients.auditors.filter(a => a.selected).length === 0 && confirmationRecipients.clients.filter(c => c.selected).length === 0)}
            >
              {sendingNotifications ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> Confirm & {sendMode === 'immediate' ? 'Send' : 'Queue'}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Notification Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="overflow-hidden flex flex-col dialog-resizable"
          style={{ width: '56rem', maxHeight: '85vh' }}
          data-testid="notification-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Notification Preview — {previewBatchName}</DialogTitle>
            <DialogDescription>
              {isSeniorScheduler
                ? 'This is how the notification email will appear to everyone once you Publish. Review the changes below before releasing.'
                : 'This is how the notification email will appear to reviewers. Review the changes below before submitting.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-md bg-white">
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full min-h-[400px] border-0"
              style={{ height: '500px' }}
              sandbox=""
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="preview-close-btn">
              Close
            </Button>
            {isSeniorScheduler ? (
              // iter476 — SS Publish (submit + approve + release).
              <Button
                onClick={async () => {
                  setPreviewOpen(false);
                  if (previewBatchId) await handlePublishBatch(previewBatchId);
                }}
                disabled={publishingBatch}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="preview-publish-btn"
              >
                {publishingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Confirm &amp; Publish
              </Button>
            ) : (
              <Button
                onClick={handlePreviewAndSubmit}
                disabled={submittingBatch}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="preview-submit-btn"
              >
                {submittingBatch ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Confirm &amp; Submit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpublish Jobs Dialog — Admin / Senior Scheduler / Scheduler only */}
      <UnpublishJobsDialog
        open={unpublishDialogOpen}
        onOpenChange={setUnpublishDialogOpen}
        month={startDate ? parseInt(startDate.split('-')[1]) : new Date().getMonth() + 1}
        year={startDate ? parseInt(startDate.split('-')[0]) : new Date().getFullYear()}
        jobs={jobs}
        sites={sites}
        auditors={auditors}
        clients={clients}
        onSuccess={(result) => {
          toast({
            title: 'Jobs Unpublished',
            description: result.message,
          });
          // Refresh the schedule view
          loadData();
        }}
      />
      {/* Combined Approve & Publish dialog — accessible from Schedule page too */}
      <ApprovePublishDialog
        open={apDialogOpen}
        onOpenChange={setApDialogOpen}
        onActionComplete={() => {
          loadData();
        }}
      />
    </div>
  );
}
// — Inline multi-select for "which auditors are assigned to
// this scope?" used in the per-scope completion section of the job
// detail dialog. Keeps the UX consistent with the existing top-level
// auditor checklist but limited to a single scope.
//
// iter462 — Rewritten to use Radix Popover (portal-based) so the list
// escapes the parent dialog's `overflow-hidden` clip. Previously, an
// inline `absolute`-positioned popup was being clipped by the dialog's
// scroll container which made the auditor list appear as if it had no
// scrollbar. The popover now portals to <body>, giving it its own
// bounded scroll region regardless of ancestor overflow.
function ScopeAuditorPicker({ jobId, scope, auditors, selectedIds, onChange }) {
  const [open, setOpen] = React.useState(false);
  const selectedNames = (auditors || [])
    .filter(a => selectedIds.includes(a.id))
    .map(a => `${a.first_name} ${a.last_name}`)
    .join(', ');
  const toggle = (aid) => {
    const next = selectedIds.includes(aid)
      ? selectedIds.filter(x => x !== aid)
      : [...selectedIds, aid];
    onChange(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 text-xs w-full text-left px-2 border rounded-md bg-white hover:bg-slate-50 truncate"
          data-testid={`scope-auditor-trigger-${scope.toLowerCase()}-${jobId}`}
        >
          {selectedNames || <span className="text-muted-foreground">Assign auditors…</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-64 max-h-[280px] overflow-y-auto p-1 text-xs"
        data-testid={`scope-auditor-menu-${scope.toLowerCase()}-${jobId}`}
      >
        {(auditors || []).length === 0 && (
          <div className="px-2 py-1 text-slate-400">No auditors available</div>
        )}
        {(auditors || []).map(a => {
          const checked = selectedIds.includes(a.id);
          return (
            <label
              key={a.id}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer"
              data-testid={`scope-auditor-opt-${scope.toLowerCase()}-${a.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(a.id)}
                className="h-3 w-3"
              />
              <span>{a.first_name} {a.last_name}</span>
            </label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
